import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight, ChevronDown, ChevronUp, Building2, DollarSign, Clock, FileText, ChevronLeft, ChevronRight, X } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useViewportTier } from "@/hooks/useViewportTier";
import { useDignityAudio } from "@/hooks/useDignityAudio";
import { DignityNarrationPlayer } from "@/components/dignity/DignityNarrationPlayer";
import { toast } from "sonner";
import { scenarioStorage } from "@/lib/scenarioStorage";
import { batchedIn } from "@/lib/batchedQuery";
import { SCENARIO_CATEGORIES, scenarioMatchesCategory, getCategoryByKey } from "@/config/scenarioCategories";
import { LazyCategoryRow } from "@/components/LazyCategoryRow";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { CreateScenarioDialog, type ScenarioTemplateData } from "@/components/CreateScenarioDialog";
import { trackSearch, trackSearchResultClick, trackScenarioBookmark, trackScenarioShare, trackInstallApp } from "@/utils/analytics";
import { loadStaticData, loadTranslatedStaticData, useLiveDb } from "@/lib/offlineData";

// TypeScript declaration for <model-viewer> web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        "auto-rotate"?: boolean | string;
        "camera-controls"?: boolean | string;
        "shadow-intensity"?: string;
        "environment-image"?: string;
        exposure?: string;
        "rotation-per-second"?: string;
        "interaction-prompt"?: string;
        "auto-rotate-delay"?: string;
        "camera-orbit"?: string;
        ar?: boolean | string;
      };
    }
  }
}

// Hero video options
const HERO_VIDEOS = [
  { id: "faded", label: "Faded Flag", src: "/consumer/hero-flag-faded.mp4" },
  { id: "full", label: "Full Color Flag", src: "/consumer/hero-flag-full.mp4" },
] as const;

/*
 * Patriotic design tokens — computed inside component for dark/light switching
 * Light: cream bg, dark text | Dark: navy bg, cream text
 */
// Homepage theme shape. Keep this stable — ~160 sites in this file read
// from a T object that matches this signature. The brand-aware factory
// below maps each active brand to a concrete instance, so swapping brands
// changes the visual identity without touching individual style sites.
interface HomepageTheme {
  bg: string;
  bgDark: string;
  headerBg: string;
  searchBg: string;
  navLogo: string;
  text: string;
  textMuted: string;
  green: string;
  gold: string;
  red: string;
  blue: string;
  border: string;
  hoverBg: string;
  imageBg: string;
  buttonBorder: string;
  shadow: string;
  cardBg: string;
  cardBorder: string;
}

/**
 * Build a per-brand homepage theme. Field semantics:
 *   red    — alert/expensive emphasis (severity 7+, urgency, price-high)
 *   blue   — primary brand-contrast color (nav logo, hero accents)
 *   gold   — accent color for bookmarks/hover states
 *   green  — semantic green for deals/savings (universally green)
 *
 * AHP keeps the patriotic palette. Luigi's reskinned to a cyberpunk-dark
 * aesthetic so the matrix-green/blood-red/cyan brand reads correctly
 * without patriotic bleed-through. AntiGreed gets cream-on-deep-green.
 */
function buildHomepageTheme(brandKey: 'ahp' | 'luigis' | 'antigreed', isDark: boolean): HomepageTheme {
  if (brandKey === 'luigis') {
    // Luigi's is intentionally a cyberpunk/Matrix aesthetic — always dark.
    return {
      bg: "#000000",
      bgDark: "#0a0a0a",
      headerBg: "#000000",
      searchBg: "#0a0a0a",
      navLogo: "#00ff00",            // matrix-green primary
      text: "#ffffff",
      textMuted: "rgba(255,255,255,0.55)",
      green: "#00ff00",              // semantic green doubles as primary
      gold: "#00ffff",               // cyan accent stands in for "gold"
      red: "#ff0000",                // blood-red alert
      blue: "#00ff00",               // primary brand contrast = matrix-green
      border: "rgba(0,255,0,0.18)",
      hoverBg: "rgba(255,0,0,0.10)",
      imageBg: "rgba(255,255,255,0.05)",
      buttonBorder: "rgba(0,255,0,0.40)",
      shadow: "rgba(0,255,0,0.20)",
      cardBg: "#0a0a0a",
      cardBorder: "rgba(0,255,0,0.18)",
    };
  }
  if (brandKey === 'antigreed') {
    return {
      bg: "#0a1408",
      bgDark: "#06100a",
      headerBg: "#0a1408",
      searchBg: "#0f1d12",
      navLogo: "#f5e6d3",
      text: "#f5e6d3",
      textMuted: "rgba(245,230,211,0.55)",
      green: "#7ddc8f",
      gold: "#e8d4b0",
      red: "#ff6600",                // warning-orange in place of red alert
      blue: "#f5e6d3",
      border: "rgba(245,230,211,0.18)",
      hoverBg: "rgba(255,102,0,0.10)",
      imageBg: "rgba(245,230,211,0.05)",
      buttonBorder: "rgba(245,230,211,0.40)",
      shadow: "rgba(0,0,0,0.45)",
      cardBg: "#0f1d12",
      cardBorder: "rgba(245,230,211,0.14)",
    };
  }
  // Default: AHP (American Hospital Prices) — original patriotic palette.
  return isDark ? {
    bg: "#0a1628",
    bgDark: "#050e1a",
    headerBg: "#0a1628",
    searchBg: "#0d1e35",
    navLogo: "#FAF9F6",
    text: "#FAF9F6",
    textMuted: "rgba(250,249,246,0.55)",
    green: "#4ade80",
    gold: "#C5A55A",
    red: "#B22234",
    blue: "#5B9BD5",   // Lighter patriotic blue — readable on dark navy bg
    border: "rgba(250,249,246,0.12)",
    hoverBg: "rgba(178,34,52,0.10)",
    imageBg: "rgba(250,249,246,0.05)",
    buttonBorder: "rgba(250,249,246,0.35)",
    shadow: "rgba(0,0,0,0.35)",
    cardBg: "#0d1e35",
    cardBorder: "rgba(250,249,246,0.10)",
  } : {
    bg: "#FAF9F6",
    bgDark: "#002868",
    headerBg: "#ffffff",
    searchBg: "#ffffff",
    navLogo: "#002868",
    text: "#1B1B1B",
    textMuted: "rgba(27,27,27,0.62)",
    green: "#2b5e49",
    gold: "#C5A55A",
    red: "#B22234",
    blue: "#002868",
    border: "rgba(27,27,27,0.12)",
    hoverBg: "rgba(178,34,52,0.04)",
    imageBg: "rgba(27,27,27,0.05)",
    buttonBorder: "rgba(27,27,27,0.40)",
    shadow: "rgba(0,0,0,0.08)",
    cardBg: "#ffffff",
    cardBorder: "rgba(27,27,27,0.08)",
  };
}

// Serif + sans font stacks (Instrument fonts loaded via <link> in index.html)
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Stable empty array for useQuery default — avoids creating a new [] reference every render
const EMPTY_SCENARIOS: ScenarioData[] = [];

// Format currency compactly (pure function — no component state needed)
function fmtPrice(n: number | null | undefined) {
  if (!n || n <= 0) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

interface ScenarioProcedure {
  code: string;
  code_type: string;
  description: string;
  phase: string;
  quantity: number;
  estimated_cost: number;
}

interface ScenarioData {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  color: string;
  duration: string;
  total_procedures: number;
  phases: string[];
  severity_level: number | null;
  calculated_avg_cost: number;
  calculated_min_cost: number;
  calculated_max_cost: number;
  phaseBreakdown: Record<string, number>;
  phaseProcedures: Record<string, ScenarioProcedure[]>;
  tags: string[];
  created_by: string | null;
  is_verified: boolean;
}


const SEARCH_CATEGORIES = [
  { value: "scenario", labelKey: "scenario", placeholderKey: "scenarioPlaceholder" },
  { value: "procedure", labelKey: "procedure", placeholderKey: "procedurePlaceholder" },
  { value: "cpt", labelKey: "cpt", placeholderKey: "cptPlaceholder" },
  { value: "hcpcs", labelKey: "hcpcs", placeholderKey: "hcpcsPlaceholder" },
  { value: "rev", labelKey: "rev", placeholderKey: "revPlaceholder" },
  { value: "ms-drg", labelKey: "msDrg", placeholderKey: "msDrgPlaceholder" },
] as const;

type SearchCategoryValue = typeof SEARCH_CATEGORIES[number]["value"];


export const ConsumerHomepage = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const { t } = useTranslation(['homepage', 'common']);
  const catLabel = (key: string) => t(`homepage:hero.searchCategories.${key}`);
  const catPlaceholder = (key: string) => t(`homepage:hero.searchCategories.${key}`);
  // Brand-aware homepage theme. AHP gets patriotic; Luigi's gets cyberpunk-
  // dark; AntiGreed gets cream-on-deep-green. The 160+ T.foo references
  // below all flow through this object, so swapping brands cleanly reskins
  // the whole homepage without touching individual style sites.
  const T = useMemo(
    () => buildHomepageTheme(brand.key, isDark),
    [brand.key, isDark]
  );
  const navigate = useNavigate();

  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [loadingFading, setLoadingFading] = useState(false);
  const [procedure, setProcedure] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [searchCategory, setSearchCategory] = useState<SearchCategoryValue>("scenario");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const [pillsCanScrollLeft, setPillsCanScrollLeft] = useState(false);
  const [pillsCanScrollRight, setPillsCanScrollRight] = useState(false);

  // Shared Web Audio — extracted to useDignityAudio hook
  const { playAudioSrc, stopAudio, volume: dignityVolume, setVolume: setDignityVolume, preloadUrls, gainNodeRef } = useDignityAudio(0.5);
  const [audioPopupOpen, setAudioPopupOpen] = useState(false);

  // Dignity narration — ElevenLabs MP3 lines
  const [dignityNarrating, setDignityNarrating] = useState(false);
  const [dignityDisplayText, setDignityDisplayText] = useState("");
  const dignityTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dignityAudioRef = useRef<HTMLAudioElement | null>(null);

  const DIGNITY_LINES: { src: string; display: string }[] = [
    { src: "/consumer/dignity_intro_1.mp3", display: "Hey there. I'm Dignity — America's healthcare advisor. I'm just a bill, but not just any bill." },
    { src: "/consumer/dignity_intro_2.mp3", display: "I'm the accumulation of every excess charge, every inflated markup across nine thousand American hospitals. And I decided it was time to fight back." },
    { src: "/consumer/dignity_intro_3.mp3", display: "I've got educational guides on thousands of procedures — plain English, real prices, no runaround. I break down every billing code and every question you've been afraid to ask." },
    { src: "/consumer/dignity_intro_4.mp3", display: "I'm not just an advisor. I represent a movement — the belief that healthcare is a human right, not a luxury that bankrupts families." },
    { src: "/consumer/dignity_intro_5.mp3", display: "My dream? To one day become a real bill — legislation that caps hospital prices and brings dignity to all Americans. So... let's get started." },
  ];

  const stopDignityNarration = () => {
    stopAudio();
    if (dignityTimerRef.current) clearInterval(dignityTimerRef.current);
    setDignityNarrating(false);
  };

  const startDignityNarration = () => {
    console.log('[Dignity] startDignityNarration called');
    stopDignityNarration();
    setDignityNarrating(true);
    setDignityDisplayText("");

    const lines = DIGNITY_LINES;
    console.log('[Dignity] lines to play:', lines.length, lines.map(l => l.src));

    const playLine = (idx: number) => {
      if (idx >= lines.length) { console.log('[Dignity] All lines done'); setDignityNarrating(false); return; }
      const line = lines[idx];
      console.log(`[Dignity] Playing line ${idx}: ${line.src}`);
      setDignityDisplayText("");

      // Typewriter
      let charIdx = 0;
      if (dignityTimerRef.current) clearInterval(dignityTimerRef.current);
      dignityTimerRef.current = setInterval(() => {
        charIdx++;
        setDignityDisplayText(line.display.slice(0, charIdx));
        if (charIdx >= line.display.length && dignityTimerRef.current) clearInterval(dignityTimerRef.current);
      }, 32);

      playAudioSrc(line.src, () => { console.log(`[Dignity] Line ${idx} ended`); playLine(idx + 1); });
    };

    playLine(0);
  };

  // Problem-statement karaoke narration
  const [problemNarrating, setProblemNarrating] = useState(false);
  const problemAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopProblemNarration = () => {
    stopAudio();
    setProblemNarrating(false);
  };

  const startProblemNarration = () => {
    if (problemNarrating) { stopProblemNarration(); return; }
    setProblemNarrating(true);
    playAudioSrc('/consumer/dignity_problem.mp3', () => {
      setProblemNarrating(false);
    });
  };

  // Step demo carousel
  const [demoSlide, setDemoSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setDemoSlide(s => (s + 1) % 5), 5500);
    return () => clearInterval(t);
  }, []);

  // Showcase carousel (problem statement + platform feature slides)
  const [showcaseRef, showcaseApi] = useEmblaCarousel({ loop: true, align: "start" }, [Autoplay({ delay: 7000, stopOnInteraction: true, stopOnMouseEnter: true })]);
  const [showcaseSlide, setShowcaseSlide] = useState(0);
  const rankingsVideoRef = useRef<HTMLVideoElement>(null);
  const scenariosVideoRef = useRef<HTMLVideoElement>(null);
  const stateCompVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!showcaseApi) return;
    const onSelect = () => setShowcaseSlide(showcaseApi.selectedScrollSnap());
    showcaseApi.on("select", onSelect);
    return () => { showcaseApi.off("select", onSelect); };
  }, [showcaseApi]);
  // Play/pause showcase videos based on active slide
  useEffect(() => {
    const rv = rankingsVideoRef.current;
    const sv = scenariosVideoRef.current;
    if (rv) { if (showcaseSlide === 1) { rv.currentTime = 0; rv.play().catch(() => {}); } else { rv.pause(); } }
    if (sv) { if (showcaseSlide === 2) { sv.currentTime = 0; sv.play().catch(() => {}); } else { sv.pause(); } }
    const cv = stateCompVideoRef.current;
    if (cv) { if (showcaseSlide === 3) { cv.currentTime = 0; cv.play().catch(() => {}); } else { cv.pause(); } }
  }, [showcaseSlide]);

  // Top announcement banner (managed by AhpGlobalHeader now)

  // PWA install state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Detect PWA mode and platform on mount
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    setIsPWA(standalone);

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Show iOS add-to-home banner if not already installed and not recently dismissed
    if (ios && !standalone && !sessionStorage.getItem('ahp_install_dismissed')) {
      const t = setTimeout(() => setShowInstallBanner(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  // Chrome/Android native install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!sessionStorage.getItem('ahp_install_dismissed')) {
        setTimeout(() => setShowInstallBanner(true), 1500);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowInstallBanner(false);
      if (outcome === 'accepted') {
        sessionStorage.setItem('ahp_install_dismissed', '1');
        trackInstallApp('android');
      }
    } else if (isIOS) {
      setShowInstallBanner(false);
      setShowIOSModal(true);
      trackInstallApp('ios');
    }
  };

  const dismissInstallBanner = () => {
    sessionStorage.setItem('ahp_install_dismissed', '1');
    setShowInstallBanner(false);
  };

  // Hero video debug controls
  const [showDebug, setShowDebug] = useState(false);
  const [videoIdx, setVideoIdx] = useState(0);
  const [videoOpacity, setVideoOpacity] = useState(0.18);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [videoPlaybackRate, setVideoPlaybackRate] = useState(1.0);
  const [bgBlend, setBgBlend] = useState("normal");
  const videoRef = useRef<HTMLVideoElement>(null);
  const dignityViewerRef = useRef<HTMLElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load @google/model-viewer for Dignity 3D character — deferred until after page is interactive
  const [modelViewerReady, setModelViewerReady] = useState(false);
  const [webglSupported] = useState(() => {
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl'));
    } catch { return false; }
  });
  useEffect(() => {
    // Don't load 3D model while loading overlay is showing — it competes for GPU/CPU
    if (showLoadingOverlay) return;
    // Skip if WebGL is not available (software rendering, disabled GPU, etc.)
    if (!webglSupported) return;

    // Preload GLB so download starts while model-viewer JS loads
    if (!document.querySelector('link[href="/consumer/dignity_bill.glb"]')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = '/consumer/dignity_bill.glb';
      link.as = 'fetch';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
    if (customElements.get('model-viewer')) {
      setModelViewerReady(true);
      return;
    }
    // Defer import to next idle period so it doesn't block input
    const timer = setTimeout(() => {
      ensureModelViewer().then(() => {
        customElements.whenDefined('model-viewer').then(() => setModelViewerReady(true));
      }).catch(() => { /* WebGL init failed — PNG fallback will show */ });
    }, 200);
    return () => clearTimeout(timer);
  }, [showLoadingOverlay, webglSupported]);

  // Oscillate Dignity ±30° like a friendly head-turn — throttled to ~10fps to avoid starving main thread
  useEffect(() => {
    const AMPLITUDE = 30;   // degrees each direction
    const PERIOD = 5;        // seconds for one full back-and-forth
    const startTime = performance.now();

    const timer = setInterval(() => {
      const elapsed = (performance.now() - startTime) / 1000;
      const theta = AMPLITUDE * Math.sin((2 * Math.PI * elapsed) / PERIOD);
      const viewer = dignityViewerRef.current as any;
      if (viewer?.cameraOrbit !== undefined) {
        viewer.cameraOrbit = `${theta}deg 75deg 105%`;
      }
    }, 100); // 10fps — smooth enough for a gentle oscillation

    return () => clearInterval(timer);
  }, []);

  // Preload all Dignity audio files — deferred until page is interactive
  useEffect(() => {
    if (showLoadingOverlay) return;
    const timer = setTimeout(() => {
      preloadUrls([
        '/consumer/dignity_intro_1.mp3',
        '/consumer/dignity_intro_2.mp3',
        '/consumer/dignity_intro_3.mp3',
        '/consumer/dignity_intro_4.mp3',
        '/consumer/dignity_intro_5.mp3',
        '/consumer/dignity_problem.mp3',
      ]);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showLoadingOverlay, preloadUrls]);

  // Category pills scroll arrow visibility
  const updatePillsScroll = useCallback(() => {
    const el = pillsRef.current;
    if (!el) return;
    setPillsCanScrollLeft(el.scrollLeft > 4);
    setPillsCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = pillsRef.current;
    if (!el) return;
    updatePillsScroll();
    el.addEventListener("scroll", updatePillsScroll, { passive: true });
    const ro = new ResizeObserver(updatePillsScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updatePillsScroll);
      ro.disconnect();
    };
  }, [updatePillsScroll]);

  const scrollPills = (dir: "left" | "right") => {
    const el = pillsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  // Ctrl+D toggles debug panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "d") {
        e.preventDefault();
        setShowDebug((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Sync playback rate
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = videoPlaybackRate;
  }, [videoPlaybackRate, videoIdx]);

  // Expanded phases tracker: { scenarioId: Set<phaseName> }
  const [expandedPhases, setExpandedPhases] = useState<Record<string, Set<string>>>({});

  const togglePhase = (scenarioId: string, phase: string) => {
    setExpandedPhases((prev) => {
      const curr = new Set(prev[scenarioId] || []);
      if (curr.has(phase)) curr.delete(phase);
      else curr.add(phase);
      return { ...prev, [scenarioId]: curr };
    });
  };

  // Viewport tier for responsive layout
  const viewportTier = useViewportTier();
  const scenariosPerPage = viewportTier === "wide" ? 9 : 6;

  // Scenarios grid: search + pagination (state only — derived values below useQuery)
  const [scenarioSearch, setScenarioSearch] = useState("");
  const [scenarioPage, setScenarioPage] = useState(0);
  const scenarioGridRef = useRef<HTMLDivElement>(null);
  const scenarioEndRef = useRef<HTMLDivElement>(null);
  const [stickySearchVisible, setStickySearchVisible] = useState(false);
  const stickyInputRef = useRef<HTMLInputElement>(null);
  const [stickyAcOpen, setStickyAcOpen] = useState(false);
  const [stickyAcSelected, setStickyAcSelected] = useState(-1);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  // Header height managed by AhpGlobalHeader now

  // Category filter + social features
  const [activeCategory, setActiveCategory] = useState("all");
  const [bookmarks, setBookmarks] = useState<Set<string>>(() => new Set(scenarioStorage.getBookmarks()));
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [templateScenario, setTemplateScenario] = useState<ScenarioTemplateData | null>(null);

  const handleToggleBookmark = useCallback((id: string) => {
    const isNowBookmarked = scenarioStorage.toggleBookmark(id);
    setBookmarks(new Set(scenarioStorage.getBookmarks()));
    toast.success(isNowBookmarked ? "Saved to bookmarks" : "Removed from bookmarks", { duration: 2000 });
    trackScenarioBookmark(id, '', isNowBookmarked);
  }, []);

  const handleShare = useCallback((scenario: ScenarioCardData) => {
    const slug = scenario.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const url = `${window.location.origin}/scenarios/${scenario.id}/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Link copied to clipboard", { duration: 2000 });
    }).catch(() => {
      toast.error("Failed to copy link");
    });
    trackScenarioShare(scenario.id, 'copy_link');
  }, []);

  const handleUseAsTemplate = useCallback((scenario: ScenarioCardData) => {
    const procs = Object.values(scenario.phaseProcedures).flat();
    setTemplateScenario({
      name: scenario.name,
      description: scenario.description,
      duration: scenario.duration,
      tags: scenario.tags,
      procedures: procs.map((p: any) => ({
        code: p.code,
        codeType: p.code_type,
        description: p.description,
        phase: p.phase,
        quantity: p.quantity,
        estimatedCost: p.estimated_cost,
      })),
    });
    setCreateDialogOpen(true);
  }, []);

  // Embla carousel for ultra-wide mode — progressive loading (15 at a time)
  const CAROUSEL_INITIAL = 15;
  const CAROUSEL_MORE = 15;
  const [carouselVisibleCount, setCarouselVisibleCount] = useState(CAROUSEL_INITIAL);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, align: "start", skipSnaps: false },
    [Autoplay({ delay: 5000, stopOnMouseEnter: true })]
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());

    // Progressive load: when near the end, add more slides
    const snapCount = emblaApi.scrollSnapList().length;
    const selectedIdx = emblaApi.selectedScrollSnap();
    if (selectedIdx >= snapCount - 3) {
      setCarouselVisibleCount((prev) => prev + CAROUSEL_MORE);
    }
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onEmblaSelect();
    emblaApi.on("select", onEmblaSelect);
    emblaApi.on("reInit", onEmblaSelect);
  }, [emblaApi, onEmblaSelect]);

  // Re-init Embla when visible count grows (new slides added)
  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [carouselVisibleCount, emblaApi]);

  // Sticky search bar: show when scenario header scrolls off top, hide when past scenario area
  useEffect(() => {
    const headerEl = scenarioGridRef.current;
    const endEl = scenarioEndRef.current;
    if (!headerEl || !endEl) return;

    const handleScroll = () => {
      const headerRect = headerEl.getBoundingClientRect();
      const endRect = endEl.getBoundingClientRect();
      // Show when header top is above viewport AND end sentinel is still below viewport top
      const show = headerRect.top < -60 && endRect.top > 60;
      setStickySearchVisible(show);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset page + carousel visible count when search or category changes
  useEffect(() => { setScenarioPage(0); setCarouselVisibleCount(CAROUSEL_INITIAL); }, [scenarioSearch, activeCategory]);

  // Re-init Embla when search filter changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
      emblaApi.scrollTo(0);
    }
  }, [scenarioSearch, emblaApi]);

  const liveDb = useLiveDb();
  const { data: stats } = useQuery({
    queryKey: ["consumer-stats", liveDb],
    queryFn: async () => {
      if (!liveDb) return { hospitals: 9582 };
      const { count: hospitalCount } = await supabase
        .from("hospitals")
        .select("id", { count: "exact", head: true });
      return { hospitals: hospitalCount ?? 9582 };
    },
    staleTime: 1000 * 60 * 60,
  });

  // Fetch real scenarios with costs and procedures (static JSON first, Supabase fallback)
  const { data: scenarios = EMPTY_SCENARIOS, isLoading: scenariosLoading } = useQuery({
    queryKey: ["consumer-scenarios-v2"],
    queryFn: async (): Promise<ScenarioData[]> => {
      // Try lightweight index first (has pre-computed phaseBreakdown, no raw procedures/AI text)
      try {
        const staticData = await loadStaticData<{ generated_at: string; data: any[] }>('scenarios_index.json');
        if (staticData && Array.isArray(staticData.data) && staticData.data.length > 0) {
          // Filter out catalog-hidden scenarios (e.g., DRG base scenarios used only for hospital pricing)
          const visible = staticData.data.filter((s: any) => !s.hidden);
          console.log(`[ConsumerHomepage] Using static scenarios (${visible.length} visible / ${staticData.data.length} total)`);
          return visible.map((s: any) => {
            // Use pre-computed phaseBreakdown from index, fall back to computing from procedures
            const phaseBreakdown: Record<string, number> = (s.phaseBreakdown && Object.keys(s.phaseBreakdown).length > 0)
              ? { ...s.phaseBreakdown }
              : {};
            const phaseProcedures: Record<string, ScenarioProcedure[]> = {};
            const procs = s.procedures || [];
            if (Object.keys(phaseBreakdown).length === 0) {
              for (const p of procs) {
                const ph = p.phase || "General";
                phaseBreakdown[ph] = (phaseBreakdown[ph] || 0) + (p.estimatedCost || 0) * (p.quantity || 1);
              }
            }
            for (const p of procs) {
              const ph = p.phase || "General";
              (phaseProcedures[ph] ??= []).push({
                code: p.code,
                code_type: p.codeType,
                description: p.description,
                phase: p.phase,
                quantity: p.quantity || 1,
                estimated_cost: p.estimatedCost || 0,
              });
            }
            const avgCost = s.estimatedCost || Object.values(phaseBreakdown).reduce((a: number, b: number) => a + b, 0);
            return {
              id: s.id,
              name: s.name,
              description: s.description || "",
              icon_name: s.iconName || "Activity",
              color: s.color || T.red,
              duration: s.duration || "",
              total_procedures: s.totalProcedures || procs.length,
              phases: (Array.isArray(s.phases) ? s.phases : Object.keys(phaseBreakdown))
                .map((p: any) => typeof p === 'string' ? p : (p?.name || p?.phase || String(p))),
              severity_level: s.severityLevel ?? null,
              calculated_avg_cost: avgCost,
              calculated_min_cost: avgCost * 0.6,
              calculated_max_cost: avgCost * 2.5,
              phaseBreakdown,
              phaseProcedures,
              tags: s.tags || [],
              created_by: s.createdBy || null,
              is_verified: s.isVerified || false,
            } as ScenarioData;
          });
        }
      } catch (e) {
        console.warn('[ConsumerHomepage] Static data failed, falling back to API:', e);
      }

      // Supabase fallback.
      //
      // Explicit column list, NOT select("*"): the four ai_* columns are ~51 MB
      // uncompressed across the catalog and this page renders none of them.
      // select("*") makes this response 68 MB and is what drives the PostgREST
      // container into its 2 GiB limit; these 13 columns are 8.7 MB.
      const { data: scenariosData } = await supabase
        .from("scenarios")
        .select("id,name,description,icon_name,color,duration,total_procedures,estimated_cost,phases,severity_level,tags,created_by,is_verified")
        .eq("is_active", true)
        .order("created_at", { ascending: true });

      if (!scenariosData?.length) return [];

      const ids = scenariosData.map((s) => s.id);

      // Fetch procedures + costs in parallel (batched to avoid URL length limits)
      const [procsRes, costsRes] = await Promise.all([
        batchedIn(
          "scenario_procedures",
          "scenario_id",
          ids,
          "scenario_id, code, code_type, description, phase, quantity, estimated_cost"
        ),
        batchedIn("scenario_costs_cache", "scenario_id", ids),
      ]);

      const procsMap: Record<string, ScenarioProcedure[]> = {};
      for (const p of procsRes.data || []) {
        (procsMap[p.scenario_id] ??= []).push(p);
      }
      const costsMap: Record<string, any> = {};
      for (const c of costsRes.data || []) costsMap[c.scenario_id] = c;

      return scenariosData.map((s) => {
        const cost = costsMap[s.id];
        const procs = procsMap[s.id] || [];
        const phaseBreakdown: Record<string, number> = {};
        const phaseProcedures: Record<string, ScenarioProcedure[]> = {};

        for (const p of procs) {
          const ph = p.phase || "General";
          phaseBreakdown[ph] = (phaseBreakdown[ph] || 0) + (p.estimated_cost || 0) * (p.quantity || 1);
          (phaseProcedures[ph] ??= []).push(p);
        }

        // Use real costs from cache if available, else sum estimated
        const fallbackCost = Object.values(phaseBreakdown).reduce((a, b) => a + b, 0);

        return {
          id: s.id,
          name: s.name,
          description: s.description,
          icon_name: s.icon_name || "Activity",
          color: s.color || T.red,
          duration: s.duration || "",
          total_procedures: s.total_procedures || procs.length,
          phases: (Array.isArray(s.phases) ? s.phases : Object.keys(phaseBreakdown))
            .map((p: any) => typeof p === 'string' ? p : (p?.name || p?.phase || String(p))),
          severity_level: s.severity_level,
          calculated_avg_cost: cost?.real_total_cost || s.estimated_cost || fallbackCost,
          calculated_median_cost: cost?.real_median_cost || cost?.real_total_cost || s.estimated_cost || fallbackCost,
          calculated_min_cost: cost?.real_min_cost || fallbackCost * 0.6,
          calculated_max_cost: cost?.real_max_cost || fallbackCost * 2.5,
          phaseBreakdown: (typeof cost?.phase_costs === 'object' && cost?.phase_costs) ? cost.phase_costs : phaseBreakdown,
          phaseBreakdownMedian: (typeof cost?.phase_costs_median === 'object' && cost?.phase_costs_median) ? cost.phase_costs_median : null,
          phaseProcedures,
          tags: (s.tags as string[]) || [],
          created_by: s.created_by || null,
          is_verified: s.is_verified || false,
        };
      });
    },
    staleTime: 30 * 60 * 1000,
  });

  // Fade out loading screen once scenarios arrive — keep overlay in DOM during fade
  useEffect(() => {
    if (!scenariosLoading && scenarios.length > 0 && !loadingFading) {
      setLoadingFading(true);
      const timer = setTimeout(() => setShowLoadingOverlay(false), 900);
      return () => clearTimeout(timer);
    }
  }, [scenariosLoading, scenarios.length]);

  // Safety: force-remove loading overlay after 6 seconds regardless
  useEffect(() => {
    const safety = setTimeout(() => {
      if (showLoadingOverlay) {
        setLoadingFading(true);
        setTimeout(() => setShowLoadingOverlay(false), 500);
      }
    }, 6000);
    return () => clearTimeout(safety);
  }, []);

  // Re-check pills overflow when scenario data changes (pills appear/disappear)
  useEffect(() => {
    const t = setTimeout(updatePillsScroll, 100);
    return () => clearTimeout(t);
  }, [scenarios.length, updatePillsScroll]);

  // Derived: filtered + paginated scenarios (must be after useQuery)
  // Memoized to avoid re-filtering 500+ scenarios on every render (scroll events, carousel, intervals)
  const activeCat = useMemo(() => getCategoryByKey(activeCategory) || SCENARIO_CATEGORIES[0], [activeCategory]);

  const filteredScenarios = useMemo(() => scenarios.filter((s) => {
    if (!scenarioMatchesCategory(s.tags, activeCat)) return false;
    if (!scenarioSearch) return true;
    const q = scenarioSearch.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.phases.some((p) => (typeof p === 'string' ? p : String(p)).toLowerCase().includes(q))
    );
  }), [scenarios, activeCat, scenarioSearch]);

  // Sticky bar autocomplete: top 6 matching scenarios for typeahead
  const stickyAcResults = useMemo(() => scenarioSearch.trim().length >= 2
    ? scenarios
        .filter(s => {
          const q = scenarioSearch.toLowerCase();
          return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
        })
        .slice(0, 6)
    : []
  , [scenarios, scenarioSearch]);

  // Open sticky autocomplete when there are results and the sticky bar is visible
  useEffect(() => {
    if (stickySearchVisible && stickyAcResults.length > 0 && scenarioSearch.trim().length >= 2) {
      setStickyAcOpen(true);
    } else {
      setStickyAcOpen(false);
    }
    setStickyAcSelected(-1);
  }, [scenarioSearch, stickySearchVisible, stickyAcResults.length]);

  const totalScenarioPages = Math.ceil(filteredScenarios.length / scenariosPerPage);
  const paginatedScenarios = useMemo(() => filteredScenarios.slice(
    scenarioPage * scenariosPerPage,
    (scenarioPage + 1) * scenariosPerPage
  ), [filteredScenarios, scenarioPage, scenariosPerPage]);

  // Ultrawide carousel: progressively loaded slice of filtered scenarios
  const carouselScenarios = useMemo(
    () => filteredScenarios.slice(0, carouselVisibleCount),
    [filteredScenarios, carouselVisibleCount],
  );

  // Pre-compute category → scenarios map (avoids 15× .filter() on every render)
  const categoryScenarioMap = useMemo(() => {
    const map: Record<string, typeof scenarios> = {};
    for (const cat of SCENARIO_CATEGORIES) {
      if (cat.key === "all") continue;
      map[cat.key] = scenarios.filter((s) => scenarioMatchesCategory(s.tags, cat));
    }
    return map;
  }, [scenarios]);

  const selectedCategory = SEARCH_CATEGORIES.find((c) => c.value === searchCategory) || SEARCH_CATEGORIES[0];

  // ─── AUTOCOMPLETE STATE ───
  interface AutocompleteItem {
    id: string;
    label: string;
    sublabel?: string;
    price?: string;
    category: string;
    icon?: string; // emoji/symbol
    navigateTo?: string;
  }
  const [acResults, setAcResults] = useState<AutocompleteItem[]>([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [acSelected, setAcSelected] = useState(-1);
  const acRef = useRef<HTMLDivElement>(null);
  const acTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Bottom CTA autocomplete state (shares acResults with hero)
  const [bottomAcOpen, setBottomAcOpen] = useState(false);
  const [bottomAcSelected, setBottomAcSelected] = useState(-1);
  const bottomAcRef = useRef<HTMLDivElement>(null);
  const [bottomDropdownOpen, setBottomDropdownOpen] = useState(false);
  const bottomDropdownRef = useRef<HTMLDivElement>(null);

  // Close autocomplete on click outside (hero + bottom CTA)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (acRef.current && !acRef.current.contains(e.target as Node)) {
        setAcOpen(false);
      }
      if (bottomAcRef.current && !bottomAcRef.current.contains(e.target as Node)) {
        setBottomAcOpen(false);
      }
      if (bottomDropdownRef.current && !bottomDropdownRef.current.contains(e.target as Node)) {
        setBottomDropdownOpen(false);
      }
      if (stickyBarRef.current && !stickyBarRef.current.contains(e.target as Node)) {
        setStickyAcOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced autocomplete fetcher
  const fetchAutocomplete = useCallback(async (query: string, cat: SearchCategoryValue) => {
    const q = query.trim();
    if (q.length < 2) { setAcResults(prev => prev.length === 0 ? prev : []); setAcOpen(false); return; }

    setAcLoading(true);
    let items: AutocompleteItem[] = [];

    try {
      if (cat === "scenario") {
        // Filter in-memory scenarios (already loaded)
        const lq = q.toLowerCase();
        items = scenarios
          .filter(s => s.name.toLowerCase().includes(lq) || s.description.toLowerCase().includes(lq))
          .slice(0, 8)
          .map(s => ({
            id: s.id,
            label: s.name,
            sublabel: s.description.length > 80 ? s.description.slice(0, 80) + "..." : s.description,
            price: fmtPrice(s.calculated_avg_cost) ? `~${fmtPrice(s.calculated_avg_cost)} avg` : undefined,
            category: "Scenario",
            icon: "\u2605", // star
          }));
      } else if (cat === "procedure") {
        const { data } = await supabase
          .from("procedures")
          .select("code, code_type, name, llm_quick_name")
          .or(`name.ilike.%${q}%,llm_quick_name.ilike.%${q}%,code.ilike.%${q}%`)
          .limit(10);
        items = (data || []).map(r => ({
          id: r.code,
          label: r.llm_quick_name || r.name || r.code,
          sublabel: r.code_type ? `${r.code_type} ${r.code}` : r.code,
          category: "Procedure",
          icon: "\u2695", // medical
        }));
      } else if (cat === "cpt") {
        const { data } = await supabase
          .from("cpt_pricing_averages")
          .select("cpt_code, procedure_description, avg_gross_charge, total_hospitals")
          .eq("setting_type", "all_settings")
          .or(`cpt_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map(r => ({
          id: r.cpt_code,
          label: r.cpt_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "..." : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `CPT \u00B7 ${r.total_hospitals || 0} hospitals`,
          icon: "\uD83C\uDFE5",
        }));
      } else if (cat === "hcpcs") {
        const { data } = await supabase
          .from("hcpcs_pricing_averages")
          .select("hcpcs_code, procedure_description, avg_gross_charge, total_hospitals")
          .eq("setting_type", "all_settings")
          .or(`hcpcs_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map(r => ({
          id: r.hcpcs_code,
          label: r.hcpcs_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "..." : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `HCPCS \u00B7 ${r.total_hospitals || 0} hospitals`,
          icon: "\uD83D\uDC8A",
        }));
      } else if (cat === "rev") {
        const { data } = await supabase
          .from("revenue_pricing_averages")
          .select("revenue_code, revenue_description, avg_charge, total_hospitals")
          .or(`revenue_code.ilike.%${q}%,revenue_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map(r => ({
          id: r.revenue_code,
          label: r.revenue_code,
          sublabel: r.revenue_description?.length > 70 ? r.revenue_description.slice(0, 70) + "..." : r.revenue_description,
          price: fmtPrice(r.avg_charge) ? `${fmtPrice(r.avg_charge)} avg` : undefined,
          category: `REV \u00B7 ${r.total_hospitals || 0} hospitals`,
          icon: "\uD83C\uDFE5",
        }));
      } else if (cat === "ms-drg") {
        const exactCode = `MS-${q}`;
        const { data } = await supabase
          .from("drg_pricing_averages")
          .select("drg_code, procedure_description, avg_gross_charge, total_hospitals")
          .or(`drg_code.ilike.%${exactCode}%,drg_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map(r => ({
          id: r.drg_code,
          label: r.drg_code?.replace("MS-", "") || r.drg_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "..." : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `MS-DRG \u00B7 ${r.total_hospitals || 0} hospitals`,
          icon: "\uD83C\uDFE5",
        }));
      }
    } catch (err) {
      console.warn("Autocomplete error:", err);
    }

    setAcResults(items);
    setAcOpen(items.length > 0);
    setAcLoading(false);
    setAcSelected(-1);
    // Also update bottom CTA autocomplete if it's focused
    if (bottomAcRef.current?.contains(document.activeElement)) {
      setBottomAcOpen(items.length > 0);
      setBottomAcSelected(-1);
    }
  }, [scenarios]);

  // Trigger debounced autocomplete on input change
  useEffect(() => {
    if (acTimerRef.current) clearTimeout(acTimerRef.current);
    acTimerRef.current = setTimeout(() => {
      fetchAutocomplete(procedure, searchCategory);
    }, searchCategory === "scenario" ? 100 : 250); // Faster for in-memory scenarios
    return () => { if (acTimerRef.current) clearTimeout(acTimerRef.current); };
  }, [procedure, searchCategory, fetchAutocomplete]);

  // Handle autocomplete item selection
  const handleAcSelect = (item: AutocompleteItem) => {
    setProcedure(item.label);
    setAcOpen(false);
    setBottomAcOpen(false);
    trackSearchResultClick('autocomplete', item.id || item.label, item.label);

    // Scenarios → navigate directly to scenario detail page
    if (item.category === "Scenario" && item.id) {
      const slug = item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      navigate(`/scenarios/${item.id}/${slug}`);
      return;
    }

    // All other categories → search results page
    const params = new URLSearchParams();
    params.set("q", item.label);
    params.set("searchType", searchCategory);
    if (zipCode) params.set("zip", zipCode);
    navigate(`/search?${params.toString()}`);
  };

  // Keyboard navigation for autocomplete
  const handleAcKeyDown = (e: React.KeyboardEvent) => {
    if (!acOpen || acResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAcSelected(prev => Math.min(prev + 1, acResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAcSelected(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && acSelected >= 0) {
      e.preventDefault();
      handleAcSelect(acResults[acSelected]);
    } else if (e.key === "Escape") {
      setAcOpen(false);
    }
  };

  // Bottom CTA keyboard navigation
  const handleBottomAcKeyDown = (e: React.KeyboardEvent) => {
    if (!bottomAcOpen || acResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setBottomAcSelected(prev => Math.min(prev + 1, acResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setBottomAcSelected(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && bottomAcSelected >= 0) {
      e.preventDefault();
      handleAcSelect(acResults[bottomAcSelected]);
      setBottomAcOpen(false);
    } else if (e.key === "Escape") {
      setBottomAcOpen(false);
    }
  };

  // ─── Stable card theme object (memoized to avoid new object refs every render) ───
  const cardTheme: CardTheme = useMemo(() => ({
    cardBg: T.cardBg, border: T.border, shadow: T.shadow,
    red: T.red, gold: T.gold, green: T.green, blue: T.blue,
    text: T.text, textMuted: T.textMuted,
  }), [isDark]);

  // ─── Scenario card renderer using memoized ScenarioCard component ───
  const renderScenarioCard = useCallback((scenario: ScenarioData, compact = false) => (
    <ScenarioCard
      key={scenario.id}
      scenario={scenario}
      compact={compact}
      isStarred={bookmarks.has(scenario.id)}
      theme={cardTheme}
      onToggleBookmark={handleToggleBookmark}
      onShare={handleShare}
      onUseAsTemplate={handleUseAsTemplate}
    />
  ), [bookmarks, cardTheme, handleToggleBookmark, handleShare, handleUseAsTemplate]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAcOpen(false);
    if (procedure) trackSearch(procedure, searchCategory);
    const params = new URLSearchParams();
    if (procedure) params.set("q", procedure);
    params.set("searchType", searchCategory);
    if (zipCode) params.set("zip", zipCode);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: T.bg, color: T.text, fontFamily: sans }}>

      {/* ─── PATRIOTIC LOADING SCREEN ─── */}
      {showLoadingOverlay && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: isDark
              ? "linear-gradient(170deg, #050e1a 0%, #0a1628 40%, #0d1e35 100%)"
              : "linear-gradient(170deg, #FAF9F6 0%, #f5f0e8 40%, #FAF9F6 100%)",
            opacity: loadingFading ? 0 : 1,
            transition: "opacity 0.7s ease-out",
            pointerEvents: loadingFading ? "none" : "auto",
          }}
        >
          <style>{`
            @keyframes ahpStripeWave {
              0% { transform: scaleX(0); opacity: 0.3; }
              50% { transform: scaleX(1); opacity: 1; }
              100% { transform: scaleX(0); opacity: 0.3; }
            }
            @keyframes ahpStarPulse {
              0%, 100% { opacity: 0.3; transform: scale(0.8); }
              50% { opacity: 1; transform: scale(1.1); }
            }
            @keyframes ahpTextFade {
              0%, 100% { opacity: 0.5; }
              50% { opacity: 1; }
            }
          `}</style>

          {/* Flag stripes animation */}
          <div style={{ width: 200, marginBottom: 32 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
              <div
                key={i}
                style={{
                  height: 4,
                  marginBottom: 2,
                  borderRadius: 2,
                  background: i % 2 === 0 ? T.red : (isDark ? "rgba(250,249,246,0.15)" : "rgba(0,40,104,0.08)"),
                  transformOrigin: "left",
                  animation: `ahpStripeWave 2s ease-in-out ${i * 0.08}s infinite`,
                }}
              />
            ))}
          </div>

          {/* Stars row */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: T.gold,
                  animation: `ahpStarPulse 1.6s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>

          {/* Brand name */}
          <div style={{
            fontFamily: serif,
            fontSize: "clamp(20px, 4vw, 28px)",
            fontWeight: 400,
            color: T.text,
            marginBottom: 8,
            letterSpacing: "-0.3px",
          }}>
            American Hospital Prices
          </div>

          {/* Loading text */}
          <div style={{
            fontFamily: sans,
            fontSize: 13,
            color: T.textMuted,
            animation: "ahpTextFade 2s ease-in-out infinite",
          }}>
            Loading healthcare data...
          </div>
        </div>
      )}

      {/* Hidden audio elements — more reliable than new Audio() for autoplay */}
      <audio ref={problemAudioRef as React.RefObject<HTMLAudioElement>} preload="none" style={{ display: "none" }} />
      <audio ref={dignityAudioRef as React.RefObject<HTMLAudioElement>} preload="none" style={{ display: "none" }} />

      {/* ─── STICKY SCENARIO SEARCH BAR (appears on scroll) ─── */}
      <div
        ref={stickyBarRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 450,
          maxHeight: stickySearchVisible ? 60 : 0,
          overflow: stickySearchVisible ? "visible" : "hidden",
          opacity: stickySearchVisible ? 1 : 0,
          transform: stickySearchVisible ? "translateY(0)" : "translateY(-8px)",
          pointerEvents: stickySearchVisible ? "auto" : "none",
          transition: "opacity 0.22s ease, transform 0.22s ease, max-height 0.22s ease",
          backgroundColor: T.headerBg,
          borderBottom: `1px solid ${T.border}`,
          padding: "8px 0",
        }}
      >
        <div className="max-w-[720px] mx-auto px-4">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: `1.5px solid ${stickyAcOpen ? T.blue : T.border}`,
              borderRadius: stickyAcOpen ? "8px 8px 0 0" : 8,
              backgroundColor: T.bg,
              padding: "7px 12px",
              transition: "border-color 0.15s ease, border-radius 0.15s ease",
            }}
          >
            <Search className="w-4 h-4 flex-shrink-0" style={{ color: T.textMuted }} />
            <input
              ref={stickyInputRef}
              type="text"
              value={scenarioSearch}
              onChange={(e) => setScenarioSearch(e.target.value)}
              onKeyDown={(e) => {
                if (!stickyAcOpen || stickyAcResults.length === 0) return;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setStickyAcSelected(prev => Math.min(prev + 1, stickyAcResults.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setStickyAcSelected(prev => Math.max(prev - 1, 0));
                } else if (e.key === "Enter" && stickyAcSelected >= 0) {
                  e.preventDefault();
                  const s = stickyAcResults[stickyAcSelected];
                  const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  navigate(`/scenarios/${s.id}/${slug}`);
                  setStickyAcOpen(false);
                } else if (e.key === "Escape") {
                  setStickyAcOpen(false);
                }
              }}
              placeholder="Search scenarios..."
              className="flex-1 outline-none bg-transparent"
              style={{ fontFamily: sans, fontSize: 14, color: T.text }}
              autoComplete="off"
            />
            {scenarioSearch && (
              <button
                onClick={() => setScenarioSearch("")}
                className="p-0.5 rounded-full transition-colors hover:bg-black/5"
                style={{ color: T.textMuted }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <span
              style={{
                fontSize: 11,
                color: T.textMuted,
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {filteredScenarios.length}
            </span>
          </div>

          {/* Autocomplete dropdown */}
          {stickyAcOpen && stickyAcResults.length > 0 && (
            <div
              style={{
                position: "absolute",
                left: 16,
                right: 16,
                backgroundColor: T.cardBg,
                border: `1.5px solid ${T.blue}`,
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
                zIndex: 500,
                overflow: "hidden",
                maxHeight: 360,
                overflowY: "auto",
              }}
            >
              {stickyAcResults.map((s, idx) => {
                const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                const isSelected = stickyAcSelected === idx;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      navigate(`/scenarios/${s.id}/${slug}`);
                      setStickyAcOpen(false);
                    }}
                    onMouseEnter={() => setStickyAcSelected(idx)}
                    className="w-full text-left transition-colors"
                    style={{
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      backgroundColor: isSelected ? "rgba(178,34,52,0.04)" : "transparent",
                      borderBottom: idx < stickyAcResults.length - 1 ? `1px solid ${T.border}` : "none",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontWeight: 600,
                        color: T.green,
                        backgroundColor: "rgba(43,94,73,0.08)",
                        padding: "2px 6px",
                        borderRadius: 4,
                        flexShrink: 0,
                        minWidth: 52,
                        textAlign: "center",
                      }}
                    >
                      {fmtPrice(s.calculated_avg_cost) || "—"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: isSelected ? T.red : T.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: sans,
                        }}
                      >
                        {s.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: T.textMuted,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginTop: 1,
                        }}
                      >
                        {s.description}
                      </div>
                    </div>
                    <ArrowRight
                      style={{
                        width: 12,
                        height: 12,
                        color: isSelected ? T.red : T.border,
                        flexShrink: 0,
                        transition: "color 0.15s, transform 0.15s",
                        transform: isSelected ? "translateX(2px)" : "none",
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ─── PWA INSTALL BANNER ─── */}
      {showInstallBanner && !isPWA && (
        <div
          style={{
            position: "fixed",
            bottom: 80,
            left: 0,
            right: 0,
            zIndex: 200,
            display: "flex",
            justifyContent: "center",
            padding: "0 16px 0",
            pointerEvents: "none",
            animation: "slideUpBanner 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <style>{`@keyframes slideUpBanner { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: T.cardBg,
              borderRadius: 16,
              border: `1px solid ${T.cardBorder}`,
              borderTop: `3px solid ${T.red}`,
              boxShadow: "0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
              padding: "16px 20px 20px",
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              pointerEvents: "auto",
            }}
          >
          {/* App icon */}
          <img
            src="/consumer/icon-192.png"
            alt="AHP"
            style={{ width: 52, height: 52, borderRadius: 12, flexShrink: 0, border: `1px solid rgba(0,0,0,0.08)` }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: serif, fontSize: 17, fontWeight: 400, color: T.blue, marginBottom: 2 }}>
              American Hospital Prices
            </div>
            <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginBottom: 12, lineHeight: 1.4 }}>
              {isIOS
                ? t('homepage:pwa.iosInstallText')
                : t('homepage:pwa.androidInstallText')}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleInstallClick}
                style={{
                  background: T.blue,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 20px",
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  letterSpacing: "-0.2px",
                }}
              >
                {isIOS ? t('common:actions.howToAdd') : t('common:actions.installApp')}
              </button>
              <button
                onClick={dismissInstallBanner}
                style={{
                  background: "transparent",
                  color: T.textMuted,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "9px 16px",
                  fontFamily: sans,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {t('common:actions.notNow')}
              </button>
            </div>
          </div>
          <button
            onClick={dismissInstallBanner}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              color: T.textMuted,
              fontSize: 20,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </button>
          </div>
        </div>
      )}

      {/* ─── iOS ADD TO HOME SCREEN MODAL ─── */}
      {showIOSModal && (
        <div
          onClick={() => setShowIOSModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 300,
            display: "flex",
            alignItems: "flex-end",
            padding: 0,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              background: T.cardBg,
              borderRadius: "20px 20px 0 0",
              padding: "24px 24px 40px",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
            }}
          >
            {/* Handle bar */}
            <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.15)", margin: "0 auto 20px" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
              <img src="/consumer/icon-192.png" alt="AHP" style={{ width: 56, height: 56, borderRadius: 14, border: `1px solid rgba(0,0,0,0.08)` }} />
              <div>
                <div style={{ fontFamily: serif, fontSize: 20, color: T.blue }}>American Hospital Prices</div>
                <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted }}>americanhospitalprices.com</div>
              </div>
            </div>
            <div style={{ fontFamily: sans, fontSize: 15, color: T.text, marginBottom: 20, lineHeight: 1.6 }}>
              {t('homepage:pwa.iosModalText')}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { step: "1", text: t('homepage:pwa.iosStep1'), detail: t('homepage:pwa.iosStep1Detail') },
                { step: "2", text: t('homepage:pwa.iosStep2'), detail: t('homepage:pwa.iosStep2Detail') },
                { step: "3", text: t('homepage:pwa.iosStep3'), detail: t('homepage:pwa.iosStep3Detail') },
              ].map(({ step, text, detail }) => (
                <div key={step} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: T.blue, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: sans, fontSize: 14, fontWeight: 700,
                    flexShrink: 0,
                  }}>{step}</div>
                  <div>
                    <div style={{ fontFamily: sans, fontSize: 15, fontWeight: 600, color: T.text }}>{text}</div>
                    <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted }}>{detail}</div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowIOSModal(false)}
              style={{
                marginTop: 24,
                width: "100%",
                background: T.blue,
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "14px 0",
                fontFamily: sans,
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t('common:actions.gotIt')}
            </button>
          </div>
        </div>
      )}


      {/* ─── HERO ─── */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(64px, 10vw, 140px)",
          paddingBottom: "clamp(64px, 10vw, 140px)",
          zIndex: 10,
        }}
      >
        {/* Video / image background (overflow-hidden wrapper keeps media contained) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {videoEnabled && (
            <video
              ref={videoRef}
              key={HERO_VIDEOS[videoIdx].id}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: videoOpacity,
                mixBlendMode: bgBlend as any,
              }}
            >
              <source src={HERO_VIDEOS[videoIdx].src} type="video/mp4" />
            </video>
          )}
          {!videoEnabled && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: "url('/consumer/hero-flag-light.webp')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: videoOpacity,
              }}
            />
          )}
        </div>
        <div className="relative z-10 max-w-[1260px] mx-auto px-6 sm:px-12 text-center">
          <h1
            className="leading-[1.05] tracking-tight mb-8"
            style={{
              fontFamily: serif,
              fontSize: "clamp(42px, 7vw, 86px)",
              letterSpacing: "-1.5px",
              fontWeight: 400,
              color: T.text,
            }}
          >
            {t('homepage:hero.title1')} <em style={{ fontStyle: "italic", color: T.red }}>{t('homepage:hero.titleEmphasis')}</em><br />
            {t('homepage:hero.title2')}
          </h1>
          <p
            className="max-w-2xl mx-auto mb-12"
            style={{ fontSize: "clamp(18px, 2.5vw, 22px)", color: T.textMuted, lineHeight: 1.6 }}
          >
            {t('homepage:hero.subtitle', { hospitalCount: (stats?.hospitals ?? 9582).toLocaleString() }).split('<red>').map((part, i) => {
              if (i === 0) return part;
              const [inner, rest] = part.split('</red>');
              return <span key={i}><span style={{ color: T.red, fontWeight: 600 }}>{inner}</span>{rest}</span>;
            })}
          </p>

          {/* Search bar — with category dropdown */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto" ref={acRef} style={{ position: "relative" }}>
            <div
              className="flex flex-col items-stretch"
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                backgroundColor: T.searchBg,
                boxShadow: `0 4px 24px ${T.shadow}`,
                overflow: "visible",
                position: "relative",
              }}
            >
              {/* Top row: category dropdown + search input (always on same line) */}
              <div className="flex items-stretch flex-1">
              {/* Category dropdown */}
              <div ref={dropdownRef} className="relative flex-shrink-0" style={{ zIndex: 20 }}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((v) => !v)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-4 h-full transition-colors"
                  style={{
                    fontFamily: sans,
                    fontSize: 14,
                    fontWeight: 500,
                    color: T.blue,
                    backgroundColor: dropdownOpen ? "rgba(0,40,104,0.04)" : "transparent",
                    borderRight: `1px solid ${T.border}`,
                    borderRadius: "12px 0 0 12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>{catLabel(selectedCategory.labelKey)}</span>
                  <ChevronDown
                    className="w-4 h-4 transition-transform"
                    style={{
                      color: T.textMuted,
                      transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>
                {dropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 py-1"
                    style={{
                      backgroundColor: T.cardBg,
                      border: `1px solid ${T.border}`,
                      borderRadius: 10,
                      boxShadow: `0 8px 32px ${T.shadow}`,
                      minWidth: 180,
                      zIndex: 50,
                    }}
                  >
                    {SEARCH_CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => {
                          setSearchCategory(cat.value);
                          setDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 transition-colors flex items-center gap-2"
                        style={{
                          fontFamily: sans,
                          fontSize: 14,
                          color: searchCategory === cat.value ? T.red : T.text,
                          backgroundColor: searchCategory === cat.value ? "rgba(178,34,52,0.04)" : "transparent",
                          fontWeight: searchCategory === cat.value ? 600 : 400,
                        }}
                        onMouseEnter={(e) => {
                          if (searchCategory !== cat.value) e.currentTarget.style.backgroundColor = T.hoverBg;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = searchCategory === cat.value ? "rgba(178,34,52,0.04)" : "transparent";
                        }}
                      >
                        {searchCategory === cat.value && (
                          <span style={{ color: T.red, fontSize: 10 }}>&#9679;</span>
                        )}
                        {catLabel(cat.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search input */}
              <div className="flex items-center gap-3 flex-1 px-4 sm:px-5 min-w-0">
                <Search className="w-5 h-5 flex-shrink-0" style={{ color: T.textMuted }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={procedure}
                  onChange={(e) => setProcedure(e.target.value)}
                  onKeyDown={handleAcKeyDown}
                  onFocus={() => { if (acResults.length > 0) setAcOpen(true); }}
                  placeholder={catPlaceholder(selectedCategory.placeholderKey)}
                  className="w-full py-4 outline-none bg-transparent"
                  style={{ fontFamily: sans, fontSize: 16, color: T.text }}
                  autoComplete="off"
                />
              </div>
              </div>

              {/* Bottom row: ZIP code + submit */}
              <div className="flex items-stretch" style={{ borderTop: `1px solid ${T.border}` }}>
                <div className="flex items-center gap-2 px-5 flex-1">
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder={t('homepage:hero.zipPlaceholder')}
                    maxLength={5}
                    className="w-full py-3 sm:py-4 outline-none bg-transparent text-center sm:text-left"
                    style={{ fontFamily: sans, fontSize: 16, color: T.text }}
                  />
                </div>
                <button
                  type="submit"
                  className="px-8 py-3 sm:py-4 font-medium transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: T.red,
                    color: "#fff",
                    fontFamily: sans,
                    fontSize: 15,
                    letterSpacing: "0.5px",
                    borderRadius: "0 0 12px 0",
                  }}
                >
                  {t('common:actions.search')}
                </button>
              </div>
            </div>

            {/* ─── AUTOCOMPLETE DROPDOWN ─── */}
            {acOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 6,
                  backgroundColor: T.cardBg,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  boxShadow: `0 12px 48px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)`,
                  zIndex: 100,
                  overflow: "hidden",
                  maxHeight: 420,
                  overflowY: "auto",
                }}
              >
                {/* Category indicator */}
                <div
                  style={{
                    padding: "10px 16px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: sans,
                    color: T.blue,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    borderBottom: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: T.red, display: "inline-block" }} />
                  {catLabel(selectedCategory.labelKey)} {t('homepage:hero.results')}
                  {acLoading && (
                    <span style={{ marginLeft: "auto", fontSize: 10, color: T.textMuted, fontWeight: 400 }}>{t('common:searching')}</span>
                  )}
                </div>

                {/* Results */}
                {acResults.map((item, idx) => (
                  <button
                    key={item.id + idx}
                    type="button"
                    onClick={() => handleAcSelect(item)}
                    onMouseEnter={() => setAcSelected(idx)}
                    className="w-full text-left transition-colors"
                    style={{
                      padding: "12px 16px",
                      fontFamily: sans,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      backgroundColor: acSelected === idx ? "rgba(0,40,104,0.04)" : "transparent",
                      borderBottom: idx < acResults.length - 1 ? `1px solid rgba(0,0,0,0.04)` : "none",
                      cursor: "pointer",
                    }}
                  >
                    {/* Icon */}
                    <span
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        marginTop: 2,
                        minWidth: 24,
                        textAlign: "center",
                        filter: acSelected === idx ? "none" : "grayscale(0.3)",
                      }}
                    >
                      {item.icon || "\u2022"}
                    </span>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: T.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.label}
                        </span>
                        {item.price && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: T.green,
                              fontFamily: "'JetBrains Mono', monospace",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            {item.price}
                          </span>
                        )}
                      </div>
                      {item.sublabel && (
                        <div
                          style={{
                            fontSize: 13,
                            color: T.textMuted,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.sublabel}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: T.blue,
                          marginTop: 3,
                          fontWeight: 500,
                          opacity: 0.6,
                        }}
                      >
                        {item.category}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight
                      className="flex-shrink-0"
                      style={{
                        width: 14,
                        height: 14,
                        color: acSelected === idx ? T.red : T.textMuted,
                        marginTop: 4,
                        transition: "color 0.15s, transform 0.15s",
                        transform: acSelected === idx ? "translateX(2px)" : "none",
                      }}
                    />
                  </button>
                ))}

                {/* Keyboard hint */}
                <div
                  style={{
                    padding: "8px 16px",
                    fontSize: 11,
                    color: T.textMuted,
                    borderTop: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontFamily: sans,
                  }}
                >
                  <span>
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>&uarr;</kbd>
                    {" "}
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>&darr;</kbd>
                    {" "}to navigate
                  </span>
                  <span>
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>Enter</kbd>
                    {" "}to select
                  </span>
                  <span style={{ marginLeft: "auto" }}>
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>Esc</kbd>
                    {" "}to close
                  </span>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* ─── SHOWCASE CAROUSEL — Problem statement + platform feature slides ─── */}
      <section style={{ backgroundColor: T.bgDark, paddingTop: "clamp(48px, 6vw, 80px)", paddingBottom: "clamp(48px, 6vw, 80px)", position: "relative" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          {/* Embla viewport */}
          <div ref={showcaseRef} style={{ overflow: "hidden", borderRadius: 16 }}>
            <div style={{ display: "flex" }}>

              {/* ── Slide 1: Problem Statement (original content) ── */}
              <div style={{ flex: "0 0 100%", minWidth: 0, padding: "0 clamp(8px, 2vw, 24px)" }}>
                <div style={{ padding: "clamp(32px, 5vw, 64px) clamp(16px, 4vw, 48px)" }}>
                  <h2 style={{ fontFamily: serif, fontSize: "clamp(28px, 4.5vw, 60px)", fontWeight: 400, color: "#fff", letterSpacing: "-1px", lineHeight: 1.1, textAlign: "center", marginBottom: "clamp(28px, 4vw, 48px)" }}>
                    {t('homepage:problem.title1')}<br /><em style={{ fontStyle: "italic", color: T.gold }}>{t('homepage:problem.titleEmphasis')}</em> {t('homepage:problem.title2')}
                  </h2>
                  <div style={{ display: "flex", gap: "clamp(24px, 4vw, 48px)", alignItems: "flex-start", flexWrap: "wrap", justifyContent: "center" }}>
                    <div style={{ flexShrink: 0, width: "clamp(180px, 22vw, 240px)", textAlign: "center" }}>
                      <div className="dignity-wrap" style={{ display: "inline-block" }}>
                        {modelViewerReady && (
                          <model-viewer
                            src="/consumer/dignity_bill.glb" alt="Dignity" camera-controls
                            shadow-intensity="0.8" exposure="1.1" interaction-prompt="none"
                            camera-orbit="0deg 75deg 105%"
                            style={{ width: "clamp(180px, 22vw, 240px)", height: "clamp(220px, 28vw, 300px)", background: "transparent", display: "block" } as React.CSSProperties}
                          />
                        )}
                      </div>
                      <button
                        onClick={problemNarrating ? stopProblemNarration : startProblemNarration}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 8,
                          padding: "8px 20px", marginTop: 10,
                          background: problemNarrating ? "rgba(178,34,52,0.85)" : "rgba(197,165,90,0.12)",
                          border: `1.5px solid ${T.gold}`, borderRadius: 24,
                          color: T.gold, fontFamily: sans, fontSize: 11, fontWeight: 700,
                          letterSpacing: "1.5px", textTransform: "uppercase" as const,
                          cursor: "pointer", transition: "background 0.2s",
                        }}
                      >
                        {problemNarrating ? <><span style={{ fontSize: 10 }}>■</span> Stop</> : <><span style={{ fontSize: 14 }}>▶</span> Play</>}
                      </button>
                      <div style={{ fontFamily: sans, fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 4, letterSpacing: "0.3px" }}>
                        {t('homepage:problem.hearDignity')}
                      </div>
                    </div>
                    <div style={{ flex: "1 1 260px", paddingTop: 4 }}>
                      <p style={{ fontSize: "clamp(14px, 1.5vw, 17px)", color: "rgba(255,255,255,0.62)", lineHeight: 1.8, marginBottom: "1.1em" }}>
                        {t('homepage:problem.paragraph1')}
                      </p>
                      <p style={{ fontSize: "clamp(14px, 1.5vw, 17px)", color: "rgba(255,255,255,0.88)", lineHeight: 1.8, marginBottom: "1.1em" }}>
                        {t('homepage:problem.paragraph2').split('<gold>').map((part: string, i: number) => {
                          if (i === 0) return part;
                          const [inner, rest] = part.split('</gold>');
                          return <span key={i}><span style={{ color: T.gold, fontWeight: 700 }}>{inner}</span>{rest}</span>;
                        })}
                      </p>
                      <p style={{ fontSize: "clamp(13px, 1.3vw, 16px)", color: T.gold, lineHeight: 1.6, fontStyle: "italic", fontWeight: 500 }}>
                        {t('homepage:problem.paragraph3')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Slide 2: Hospital Rankings ── */}
              <div style={{ flex: "0 0 100%", minWidth: 0, padding: "0 clamp(8px, 2vw, 24px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "clamp(24px, 4vw, 64px)", flexWrap: "wrap", padding: "clamp(32px, 4vw, 56px) clamp(16px, 3vw, 40px)" }}>
                  {/* Text */}
                  <div style={{ flex: "1 1 260px", maxWidth: 400 }}>
                    <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: T.red, marginBottom: 12 }}>Hospital Rankings</div>
                    <h3 style={{ fontFamily: serif, fontSize: "clamp(24px, 3.5vw, 44px)", fontWeight: 400, color: "#fff", lineHeight: 1.15, marginBottom: 16 }}>
                      See which hospitals charge the most
                    </h3>
                    <p style={{ fontSize: "clamp(14px, 1.4vw, 16px)", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 24 }}>
                      Compare gross markup percentages across 9,500+ hospitals. Filter by state, see CPT/DRG/Revenue code breakdowns, and find the most egregious chargemasters in America.
                    </p>
                    <Link to="/hospital-rankings" style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 28px", background: T.red, color: "#fff",
                      borderRadius: 8, fontFamily: sans, fontSize: 14, fontWeight: 700,
                      textDecoration: "none", letterSpacing: "0.5px",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: "0 4px 16px rgba(178,34,52,0.35)",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(178,34,52,0.5)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 16px rgba(178,34,52,0.35)"; }}
                    >
                      View Rankings <ArrowRight size={16} />
                    </Link>
                  </div>
                  {/* Desktop + Phone mockup — real screenshot */}
                  <div style={{ flex: "1 1 300px", maxWidth: 580, position: "relative", display: "flex", alignItems: "flex-end", gap: 0 }}>
                    {/* Desktop frame */}
                    <div style={{ flex: "1 1 auto", borderRadius: 10, boxShadow: `0 12px 48px rgba(0,0,0,0.5)`, overflow: "hidden", border: `1px solid rgba(255,255,255,0.1)` }}>
                      {/* Browser chrome */}
                      <div style={{ background: isDark ? "#0a1628" : "#F0EEE9", padding: "6px 10px", display: "flex", alignItems: "center", gap: 5, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF5F57" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FEBC2E" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#28C840" }} />
                        <div style={{ flex: 1, background: isDark ? "#152240" : "#fff", borderRadius: 4, padding: "2px 8px", marginLeft: 6, fontFamily: sans, fontSize: 9, color: "rgba(255,255,255,0.4)" }}>
                          americanhospitalprices.com/hospital-rankings
                        </div>
                      </div>
                      {/* Static screenshot — hospital-rankings-demo.mp4 was never
                          produced (the old <video> 404'd on every homepage view and
                          showed only this poster anyway). Restore the <video> when
                          the clip exists in public/consumer/. */}
                      <img
                        src="/consumer/hospital-rankings-screenshot.png"
                        alt="Hospital rankings demo"
                        loading="lazy"
                        style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover", objectPosition: "top" }}
                      />
                    </div>
                    {/* iPhone frame */}
                    <div style={{ flex: "0 0 auto", width: 120, marginLeft: -40, marginBottom: -16, position: "relative", zIndex: 2 }}>
                      <div style={{ borderRadius: 18, boxShadow: `0 8px 32px rgba(0,0,0,0.6)`, overflow: "hidden", border: `3px solid ${isDark ? "#1a1a2e" : "#222"}`, background: isDark ? "#0a1628" : "#000" }}>
                        {/* iPhone notch */}
                        <div style={{ height: 16, background: isDark ? "#0a1628" : "#000", display: "flex", justifyContent: "center", alignItems: "flex-end", paddingBottom: 2 }}>
                          <div style={{ width: 40, height: 4, borderRadius: 2, background: isDark ? "#152240" : "#1a1a1a" }} />
                        </div>
                        <img
                          src="/consumer/rankings-mobile-screenshot.png"
                          alt="Hospital Rankings on iPhone"
                          style={{ width: "100%", display: "block" }}
                        />
                        {/* iPhone home bar */}
                        <div style={{ height: 12, background: isDark ? "#0a1628" : "#000", display: "flex", justifyContent: "center", alignItems: "center" }}>
                          <div style={{ width: 32, height: 3, borderRadius: 2, background: isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.3)" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Slide 3: Medical Scenarios ── */}
              <div style={{ flex: "0 0 100%", minWidth: 0, padding: "0 clamp(8px, 2vw, 24px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "clamp(24px, 4vw, 64px)", flexWrap: "wrap", padding: "clamp(32px, 4vw, 56px) clamp(16px, 3vw, 40px)" }}>
                  <div style={{ flex: "1 1 260px", maxWidth: 400 }}>
                    <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: T.blue, marginBottom: 12 }}>Medical Scenarios</div>
                    <h3 style={{ fontFamily: serif, fontSize: "clamp(24px, 3.5vw, 44px)", fontWeight: 400, color: "#fff", lineHeight: 1.15, marginBottom: 16 }}>
                      Plan your care, know your costs
                    </h3>
                    <p style={{ fontSize: "clamp(14px, 1.4vw, 16px)", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 24 }}>
                      Browse 200+ pre-built medical scenarios from pregnancy to knee replacement. See real pricing from hospital chargemasters, phase-by-phase breakdowns, and which hospitals are cheapest near you.
                    </p>
                    <Link to="/scenarios" style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 28px", background: T.blue, color: "#fff",
                      borderRadius: 8, fontFamily: sans, fontSize: 14, fontWeight: 700,
                      textDecoration: "none", letterSpacing: "0.5px",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: `0 4px 16px rgba(0,40,104,0.4)`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
                    >
                      Browse Scenarios <ArrowRight size={16} />
                    </Link>
                  </div>
                  <div style={{ flex: "1 1 300px", maxWidth: 520 }}>
                    <div style={{ borderRadius: 10, boxShadow: `0 12px 48px rgba(0,0,0,0.5)`, overflow: "hidden", border: `1px solid rgba(255,255,255,0.1)` }}>
                      {/* Browser chrome */}
                      <div style={{ background: isDark ? "#0a1628" : "#F0EEE9", padding: "6px 10px", display: "flex", alignItems: "center", gap: 5, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF5F57" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FEBC2E" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#28C840" }} />
                        <div style={{ flex: 1, background: isDark ? "#152240" : "#fff", borderRadius: 4, padding: "2px 8px", marginLeft: 6, fontFamily: sans, fontSize: 9, color: "rgba(255,255,255,0.4)" }}>
                          americanhospitalprices.com/scenarios
                        </div>
                      </div>
                      {/* Video clip */}
                      <video
                        ref={scenariosVideoRef}
                        src="/consumer/scenarios-demo.mp4"
                        poster="/consumer/scenarios-screenshot.png"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        style={{ width: "100%", display: "block", maxHeight: 340, objectFit: "cover", objectPosition: "top" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Slide 4: State Comparison ── */}
              <div style={{ flex: "0 0 100%", minWidth: 0, padding: "0 clamp(8px, 2vw, 24px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "clamp(24px, 4vw, 64px)", flexWrap: "wrap", padding: "clamp(32px, 4vw, 56px) clamp(16px, 3vw, 40px)" }}>
                  <div style={{ flex: "1 1 260px", maxWidth: 400 }}>
                    <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: T.gold, marginBottom: 12 }}>State Comparison</div>
                    <h3 style={{ fontFamily: serif, fontSize: "clamp(24px, 3.5vw, 44px)", fontWeight: 400, color: "#fff", lineHeight: 1.15, marginBottom: 16 }}>
                      Compare healthcare prices across all 50 states
                    </h3>
                    <p style={{ fontSize: "clamp(14px, 1.4vw, 16px)", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 24 }}>
                      Interactive maps and charts showing which states have the highest hospital markups. Drill down by procedure type, compare scenario costs, and see how US prices stack up internationally.
                    </p>
                    <Link to="/state-comparison" style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 28px", background: T.gold, color: "#002868",
                      borderRadius: 8, fontFamily: sans, fontSize: 14, fontWeight: 700,
                      textDecoration: "none", letterSpacing: "0.5px",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: `0 4px 16px rgba(197,165,90,0.35)`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
                    >
                      Explore States <ArrowRight size={16} />
                    </Link>
                  </div>
                  <div style={{ flex: "1 1 300px", maxWidth: 520 }}>
                    <div style={{ borderRadius: 10, boxShadow: `0 12px 48px rgba(0,0,0,0.5)`, overflow: "hidden", border: `1px solid rgba(255,255,255,0.1)` }}>
                      {/* Browser chrome */}
                      <div style={{ background: isDark ? "#0a1628" : "#F0EEE9", padding: "6px 10px", display: "flex", alignItems: "center", gap: 5, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF5F57" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FEBC2E" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#28C840" }} />
                        <div style={{ flex: 1, background: isDark ? "#152240" : "#fff", borderRadius: 4, padding: "2px 8px", marginLeft: 6, fontFamily: sans, fontSize: 9, color: "rgba(255,255,255,0.4)" }}>
                          americanhospitalprices.com/state-comparison
                        </div>
                      </div>
                      {/* Video clip */}
                      <video
                        ref={stateCompVideoRef}
                        src="/consumer/state-comparison-demo.mp4"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        style={{ width: "100%", display: "block", maxHeight: 340, objectFit: "cover", objectPosition: "top" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Slide 5: International Comparison ── */}
              <div style={{ flex: "0 0 100%", minWidth: 0, padding: "0 clamp(8px, 2vw, 24px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "clamp(24px, 4vw, 64px)", flexWrap: "wrap", padding: "clamp(32px, 4vw, 56px) clamp(16px, 3vw, 40px)" }}>
                  <div style={{ flex: "1 1 260px", maxWidth: 400 }}>
                    <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: T.green, marginBottom: 12 }}>International Prices</div>
                    <h3 style={{ fontFamily: serif, fontSize: "clamp(24px, 3.5vw, 44px)", fontWeight: 400, color: "#fff", lineHeight: 1.15, marginBottom: 16 }}>
                      See what the rest of the world pays
                    </h3>
                    <p style={{ fontSize: "clamp(14px, 1.4vw, 16px)", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 24 }}>
                      Compare US healthcare prices against 10 countries. The same knee replacement that costs $31,400 in the US averages $6,200 in Germany and $3,900 in South Korea.
                    </p>
                    <Link to="/international" style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 28px", background: T.green, color: "#fff",
                      borderRadius: 8, fontFamily: sans, fontSize: 14, fontWeight: 700,
                      textDecoration: "none", letterSpacing: "0.5px",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: `0 4px 16px rgba(43,94,73,0.4)`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
                    >
                      Compare Globally <ArrowRight size={16} />
                    </Link>
                  </div>
                  <div style={{ flex: "1 1 300px", maxWidth: 480 }}>
                    <div style={{ background: isDark ? "#0f1b2e" : "#fff", borderRadius: 10, boxShadow: `0 12px 48px rgba(0,0,0,0.4)`, overflow: "hidden", border: `1px solid rgba(255,255,255,0.1)` }}>
                      <div style={{ background: isDark ? "#0a1628" : "#F0EEE9", padding: "6px 10px", display: "flex", alignItems: "center", gap: 5, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF5F57" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FEBC2E" }} />
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#28C840" }} />
                        <div style={{ flex: 1, background: isDark ? "#152240" : "#fff", borderRadius: 4, padding: "2px 8px", marginLeft: 6, fontFamily: sans, fontSize: 9, color: "rgba(255,255,255,0.4)" }}>
                          americanhospitalprices.com/international
                        </div>
                      </div>
                      <div style={{ padding: "12px 14px", background: isDark ? "#0d1e35" : "#FAF9F6", minHeight: 180 }}>
                        <div style={{ fontFamily: serif, fontSize: 12, color: "#fff", marginBottom: 10 }}>Knee Replacement — US vs World</div>
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                            <span style={{ fontSize: 14 }}>🇺🇸</span>
                            <div style={{ flex: 1, height: 14, background: T.red, borderRadius: 4, display: "flex", alignItems: "center", paddingLeft: 6 }}>
                              <span style={{ fontSize: 8, color: "#fff", fontWeight: 700, fontFamily: sans }}>$31,400</span>
                            </div>
                          </div>
                          {[
                            { flag: "🇨🇦", price: "$11,600", pct: 37 },
                            { flag: "🇩🇪", price: "$6,200", pct: 20 },
                            { flag: "🇬🇧", price: "$8,100", pct: 26 },
                            { flag: "🇰🇷", price: "$3,900", pct: 12 },
                            { flag: "🇯🇵", price: "$4,800", pct: 15 },
                            { flag: "🇮🇱", price: "$7,500", pct: 24 },
                          ].map((c, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                              <span style={{ fontSize: 14 }}>{c.flag}</span>
                              <div style={{ flex: 1, height: 10, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
                                <div style={{ width: `${c.pct}%`, height: "100%", background: T.green, borderRadius: 4 }} />
                              </div>
                              <div style={{ fontSize: 8, fontFamily: "monospace", color: T.green, fontWeight: 700, width: 44, textAlign: "right" }}>{c.price}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Carousel dots + labels */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "clamp(12px, 2vw, 24px)", marginTop: 20, flexWrap: "wrap" }}>
            {[
              { label: "The Problem", color: T.gold },
              { label: "Hospital Rankings", color: T.red },
              { label: "Scenarios", color: T.blue },
              { label: "State Comparison", color: T.gold },
              { label: "vs World", color: T.green },
            ].map((dot, i) => (
              <button key={i} onClick={() => showcaseApi?.scrollTo(i)} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer", padding: "4px 6px",
                opacity: showcaseSlide === i ? 1 : 0.4,
                transition: "opacity 0.3s",
              }}>
                <div style={{
                  width: showcaseSlide === i ? 28 : 8, height: 4, borderRadius: 2,
                  background: showcaseSlide === i ? dot.color : "rgba(255,255,255,0.3)",
                  transition: "all 0.4s ease",
                }} />
                <span style={{ fontFamily: sans, fontSize: 10, fontWeight: showcaseSlide === i ? 700 : 400, color: showcaseSlide === i ? "#fff" : "rgba(255,255,255,0.4)", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>
                  {dot.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS (moved up) ─── */}
      <section
        style={{
          borderTop: `1px solid ${T.border}`,
          borderBottom: `1px solid ${T.border}`,
          paddingTop: "clamp(48px, 6vw, 96px)",
          paddingBottom: "clamp(48px, 6vw, 96px)",
        }}
      >
        <div className="w-full px-6 sm:px-10 lg:px-16">
          <h2
            className="text-center mb-16"
            style={{
              fontFamily: serif,
              fontSize: "clamp(28px, 4vw, 48px)",
              fontWeight: 400,
              letterSpacing: "-0.5px",
            }}
          >
            {t('homepage:howItWorks.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6">
            {[
              {
                num: "1",
                title: t('homepage:howItWorks.step1Title'),
                desc: t('homepage:howItWorks.step1Desc'),
                extra: null,
              },
              {
                num: "2",
                title: t('homepage:howItWorks.step2Title'),
                desc: t('homepage:howItWorks.step2Desc'),
                extra: null,
              },
              {
                num: "3",
                title: t('homepage:howItWorks.step3Title'),
                desc: t('homepage:howItWorks.step3Desc'),
                extra: { label: t('homepage:howItWorks.step3Link'), to: "/international" },
              },
              {
                num: "4",
                title: t('homepage:howItWorks.step4Title'),
                desc: t('homepage:howItWorks.step4Desc'),
                extra: null,
              },
              {
                num: "5",
                title: t('homepage:howItWorks.step5Title'),
                desc: t('homepage:howItWorks.step5Desc'),
                extra: { label: t('homepage:howItWorks.step5Link'), to: "/stories" },
              },
            ].map((step) => (
              <div key={step.num} className="text-center md:text-left">
                <div
                  className="text-5xl mb-4"
                  style={{ fontFamily: serif, color: T.red, fontWeight: 400 }}
                >
                  {step.num}.
                </div>
                <h3 className="text-xl font-medium mb-3" style={{ fontFamily: sans, color: T.text }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 16, color: T.textMuted, lineHeight: 1.65 }}>{step.desc}</p>
                {step.extra && (
                  <Link
                    to={step.extra.to}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginTop: 10,
                      fontSize: 13,
                      fontFamily: sans,
                      fontWeight: 600,
                      color: T.blue,
                      textDecoration: "none",
                      borderBottom: `1px solid ${T.blue}`,
                      paddingBottom: 1,
                      opacity: 0.85,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                  >
                    {step.extra.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STEP DEMO CAROUSEL ─── */}
      <section style={{ backgroundColor: T.bg, paddingTop: "clamp(48px, 6vw, 80px)", paddingBottom: "clamp(64px, 8vw, 112px)" }}>
        <div style={{ maxWidth: "100%", margin: "0 auto", padding: "0 clamp(16px, 4vw, 48px)" }}>

          {/* Slide frame */}
          <div style={{
            position: "relative",
            borderRadius: 14,
            overflow: "hidden",
            border: `1px solid ${T.border}`,
            boxShadow: `0 8px 48px ${T.shadow}`,
            background: isDark ? T.cardBg : "#EFECE5",
            height: "50vh",
            width: "100%",
          }}>
            {[
              {
                label: "Step 1",
                title: "Build your Scenario",
                color: T.red,
                bg: isDark ? "linear-gradient(145deg, #0d1e35 0%, #0f2340 100%)" : "linear-gradient(145deg, #FAF9F6 0%, #F0EDE5 100%)",
                details: [`Search ${scenarios.length || 208}+ pre-built medical scenarios`, "Mix procedures, medications & hospital stays", "Compare prices across 9,500+ hospitals"],
              },
              {
                label: "Step 2",
                title: "Set up your Family",
                color: T.blue,
                bg: isDark ? "linear-gradient(145deg, #0c1a30 0%, #0e1f3a 100%)" : "linear-gradient(145deg, #F5F7FA 0%, #E8EDF5 100%)",
                details: ["Add every member of your household", "Assign scenarios to each person", "See your family's total expected annual cost"],
              },
              {
                label: "Step 3",
                title: "Find Your Cheapest Route",
                color: "#7B5EA7",
                bg: isDark ? "linear-gradient(145deg, #12102a 0%, #1a1538 100%)" : "linear-gradient(145deg, #F7F4FB 0%, #EDE8F5 100%)",
                details: ["Hospital price comparison within your travel radius", "Automated reminders: which hospital for each test", "International prices: 10 countries, 1,511 procedures"],
              },
              {
                label: "Step 4",
                title: "Plan with Dignity",
                color: T.green,
                bg: isDark ? "linear-gradient(145deg, #0b1e1a 0%, #0e2820 100%)" : "linear-gradient(145deg, #F4F8F5 0%, #E5EFE8 100%)",
                details: ["AI advisor reviews your family's full picture", "Projects yearly spend & insurance fit", "Finds savings opportunities you'd never spot alone"],
              },
              {
                label: "Step 5",
                title: "Get Connected to Care",
                color: "#C5750A",
                bg: isDark ? "linear-gradient(145deg, #1a160e 0%, #231c10 100%)" : "linear-gradient(145deg, #FBF7F1 0%, #F5EBD8 100%)",
                details: ["Support groups matched to your scenario", "Financial assistance & patient programs", "Peer stories from people in your situation"],
              },
            ].map((slide, idx) => (
              <div key={idx} style={{
                position: "absolute", inset: 0,
                opacity: demoSlide === idx ? 1 : 0,
                transition: "opacity 0.9s ease",
                pointerEvents: demoSlide === idx ? "auto" : "none",
                background: slide.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "clamp(24px, 5vw, 64px)",
                gap: "clamp(24px, 4vw, 64px)",
                flexWrap: "wrap",
              }}>
                {/* Left: step info */}
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                  <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: slide.color, marginBottom: 10 }}>
                    {slide.label}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: "clamp(22px, 3vw, 40px)", fontWeight: 400, color: T.text, lineHeight: 1.15, marginBottom: 20 }}>
                    {slide.title}
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                    {slide.details.map((d, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontFamily: sans, fontSize: "clamp(13px, 1.4vw, 15px)", color: T.textMuted, lineHeight: 1.5 }}>
                        <span style={{ color: slide.color, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: realistic app screenshot mockup */}
                <div style={{ flex: "1 1 260px", maxWidth: 420 }}>
                  <div style={{
                    background: isDark ? "#0f1b2e" : "white",
                    borderRadius: 10,
                    boxShadow: `0 8px 36px ${T.shadow}`,
                    overflow: "hidden",
                    border: `1px solid ${T.border}`,
                  }}>
                    {/* Browser chrome */}
                    <div style={{ background: isDark ? "#0a1628" : "#F0EEE9", padding: "7px 10px", display: "flex", alignItems: "center", gap: 5, borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF5F57" }} />
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FEBC2E" }} />
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#28C840" }} />
                      <div style={{ flex: 1, background: isDark ? "#152240" : "white", borderRadius: 4, padding: "3px 8px", marginLeft: 6, fontFamily: sans, fontSize: 10, color: T.textMuted }}>
                        americanhospitalprices.com
                      </div>
                    </div>

                    {/* Step 1 — Scenarios browse */}
                    {idx === 0 && (
                      <div style={{ padding: "12px 14px", background: isDark ? "#0d1e35" : "#FAF9F6", minHeight: 200 }}>
                        {/* Search bar */}
                        <div style={{ display: "flex", alignItems: "center", background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.1)" : "#e5e2da"}`, borderRadius: 7, padding: "5px 9px", marginBottom: 10, gap: 6 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                          <span style={{ fontSize: 10, color: "#bbb", fontFamily: sans }}>Search scenarios — pregnancy, knee surgery…</span>
                        </div>
                        {/* Scenario cards */}
                        {[
                          { name: "Normal Pregnancy", cost: "$14,200", tag: "12 procedures · 9 months", color: T.red },
                          { name: "Knee Replacement", cost: "$31,400", tag: "8 procedures · 6–12 weeks", color: T.blue },
                          { name: "Type 2 Diabetes", cost: "$8,600", tag: "Ongoing · Annual mgmt", color: T.green },
                        ].map((s, i) => (
                          <div key={i} style={{ background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.08)" : "#ede9e0"}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: `0 1px 4px ${T.shadow}` }}>
                            <div>
                              <div style={{ fontSize: 11, fontFamily: serif, color: T.text, marginBottom: 2, fontWeight: 400 }}>{s.name}</div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontFamily: sans }}>{s.tag}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 13, fontFamily: serif, color: s.color }}>{s.cost}</div>
                              <div style={{ fontSize: 8, background: `${s.color}15`, color: s.color, padding: "1px 5px", borderRadius: 3, marginTop: 2, fontFamily: sans, fontWeight: 700, letterSpacing: "0.5px" }}>AVG</div>
                            </div>
                          </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                          <div style={{ height: 26, width: 120, background: T.red, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 9, color: "white", fontFamily: sans, fontWeight: 700, letterSpacing: "1px" }}>EXPLORE ALL {scenarios.length || 208}+</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2 — Family planner */}
                    {idx === 1 && (
                      <div style={{ padding: "12px 14px", background: isDark ? "#0d1e35" : "#FAF9F6", minHeight: 200 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, fontFamily: sans, color: T.blue, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>My Family</div>
                        {[
                          { initials: "SM", name: "Sarah M.", role: "Parent", scenario: "Type 2 Diabetes", cost: "$8,600", bg: T.red },
                          { initials: "DM", name: "David M.", role: "Parent", scenario: "Knee Replacement", cost: "$31,400", bg: T.blue },
                          { initials: "EM", name: "Emma M.", role: "Child", scenario: "Annual Checkup", cost: "$420", bg: T.gold },
                        ].map((m, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.08)" : "#ede9e0"}`, borderRadius: 8, padding: "7px 9px", marginBottom: 6, boxShadow: `0 1px 4px ${T.shadow}` }}>
                            <div style={{ width: 26, height: 26, borderRadius: "50%", background: m.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white", fontWeight: 800, flexShrink: 0, fontFamily: sans }}>{m.initials}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: sans }}>{m.name} <span style={{ color: T.textMuted, fontWeight: 400, fontSize: 9 }}>· {m.role}</span></div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontFamily: sans, marginTop: 1 }}>{m.scenario}</div>
                            </div>
                            <div style={{ fontSize: 11, color: T.red, fontFamily: serif, flexShrink: 0 }}>{m.cost}</div>
                          </div>
                        ))}
                        <div style={{ background: `linear-gradient(90deg, ${T.blue} 0%, #0038B8 100%)`, borderRadius: 8, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                          <span style={{ fontFamily: sans, fontSize: 9, color: "rgba(255,255,255,0.75)", fontWeight: 800, letterSpacing: "1.5px" }}>ANNUAL TOTAL</span>
                          <span style={{ fontFamily: serif, fontSize: 15, color: T.gold }}>$40,420</span>
                        </div>
                      </div>
                    )}

                    {/* Step 3 — Hospital route optimizer */}
                    {idx === 2 && (
                      <div style={{ padding: "12px 14px", background: isDark ? "#12102a" : "#F7F4FB", minHeight: 200 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, fontFamily: sans, color: "#7B5EA7", letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>
                          Care Route · Dallas, TX · 60 mi radius
                        </div>
                        {/* Hospital rows */}
                        {[
                          { rank: 1, name: "UT Southwestern", dist: "12 mi", procedure: "Knee Replacement", price: "$19,400", saving: "save $12k", flag: "🏆" },
                          { rank: 2, name: "Methodist Dallas", dist: "8 mi",  procedure: "Knee Replacement", price: "$24,100", saving: "save $7.3k", flag: null },
                          { rank: 3, name: "Baylor Scott & White", dist: "18 mi", procedure: "Knee Replacement", price: "$31,400", saving: null, flag: null },
                        ].map((h) => (
                          <div key={h.rank} style={{ display: "flex", alignItems: "center", gap: 7, background: h.rank === 1 ? "rgba(123,94,167,0.12)" : (isDark ? "#152240" : "white"), border: `1px solid ${h.rank === 1 ? (isDark ? "rgba(200,184,232,0.3)" : "#c8b8e8") : (isDark ? "rgba(250,249,246,0.08)" : "#ede9e0")}`, borderRadius: 8, padding: "7px 9px", marginBottom: 5, boxShadow: `0 1px 3px ${T.shadow}` }}>
                            <div style={{ width: 20, height: 20, borderRadius: "50%", background: h.rank === 1 ? "#7B5EA7" : (isDark ? "#2a2040" : "#e8e4ee"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: h.rank === 1 ? "white" : T.textMuted, fontWeight: 800, flexShrink: 0, fontFamily: sans }}>#{h.rank}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: sans }}>{h.name} <span style={{ color: T.textMuted, fontWeight: 400 }}>· {h.dist}</span></div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontFamily: sans }}>{h.procedure}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 11, fontFamily: serif, color: h.rank === 1 ? "#7B5EA7" : T.red }}>{h.price}</div>
                              {h.saving && <div style={{ fontSize: 8, color: T.green, fontWeight: 700, fontFamily: sans }}>{h.saving}</div>}
                            </div>
                          </div>
                        ))}
                        {/* International teaser */}
                        <div style={{ marginTop: 8, background: isDark ? "#152240" : "white", border: `1.5px solid ${T.blue}`, borderRadius: 8, padding: "7px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 9, fontWeight: 800, color: T.blue, fontFamily: sans, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 1 }}>🌐 Medical Tourism Option</div>
                            <div style={{ fontSize: 9, color: T.textMuted, fontFamily: sans }}>Same surgery in Germany: <span style={{ color: T.green, fontWeight: 700 }}>~$6,200</span> · 10 countries mapped</div>
                          </div>
                          <div style={{ fontSize: 9, color: T.blue, fontWeight: 700, fontFamily: sans, whiteSpace: "nowrap", marginLeft: 8 }}>See all →</div>
                        </div>
                      </div>
                    )}

                    {/* Step 4 — Dignity AI chat */}
                    {idx === 3 && (
                      <div style={{ padding: "10px 12px", background: isDark ? "#0d1e35" : "#FAF9F6", minHeight: 200 }}>
                        {/* Dignity nameplate */}
                        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 8px", background: `linear-gradient(90deg, ${T.blue}, #003AB0)`, borderRadius: 6, marginBottom: 10 }}>
                          <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, flexShrink: 0 }}>♪</div>
                          <span style={{ fontFamily: sans, fontSize: 9, fontWeight: 800, color: T.gold, letterSpacing: "2px" }}>DIGNITY · AI ADVISOR</span>
                        </div>
                        {/* Reminders strip */}
                        <div style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap" }}>
                          {["🔔 Blood panel due Feb 28", "📍 UT Southwestern booked", "💊 Metformin refill Mar 3"].map((r) => (
                            <div key={r} style={{ fontSize: 8, background: isDark ? "rgba(91,155,213,0.12)" : "rgba(0,40,104,0.07)", color: T.blue, borderRadius: 4, padding: "3px 7px", fontFamily: sans, fontWeight: 600, whiteSpace: "nowrap" }}>{r}</div>
                          ))}
                        </div>
                        {/* Chat bubbles */}
                        <div style={{ background: `linear-gradient(135deg, ${T.blue}, #003AB0)`, borderRadius: "8px 8px 8px 2px", padding: "8px 10px", marginBottom: 7, maxWidth: "88%" }}>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.92)", fontFamily: serif, lineHeight: 1.55 }}>
                            Your family's projected annual spend is <span style={{ color: T.gold }}>$40,420</span>. Routing David to UT Southwestern drops that to <span style={{ color: "#7DFFB3", fontWeight: 700 }}>$28,200</span>.
                          </div>
                        </div>
                        <div style={{ background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.1)" : "#e5e2da"}`, borderRadius: "8px 8px 2px 8px", padding: "7px 10px", marginBottom: 7, maxWidth: "76%", marginLeft: "auto" }}>
                          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: serif, lineHeight: 1.5 }}>What about getting the blood tests done cheaper?</div>
                        </div>
                        <div style={{ background: `linear-gradient(135deg, ${T.blue}, #003AB0)`, borderRadius: "8px 8px 8px 2px", padding: "8px 10px", maxWidth: "88%" }}>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.92)", fontFamily: serif, lineHeight: 1.55 }}>
                            Quest Diagnostics at 14 mi: <span style={{ color: T.gold }}>$38</span> vs hospital lab: $318. I'll set a reminder. 🗓️
                          </div>
                        </div>
                        {/* Input bar */}
                        <div style={{ marginTop: 10, display: "flex", gap: 6, alignItems: "center" }}>
                          <div style={{ flex: 1, background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.1)" : "#e5e2da"}`, borderRadius: 6, padding: "5px 9px", fontSize: 10, color: T.textMuted, fontFamily: sans }}>Ask Dignity anything…</div>
                          <div style={{ width: 26, height: 26, borderRadius: 6, background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M2 21 23 12 2 3v7l15 2-15 2v7z"/></svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 5 — Community & support */}
                    {idx === 4 && (
                      <div style={{ padding: "12px 14px", background: isDark ? "#1a160e" : "#FBF7F1", minHeight: 200 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, fontFamily: sans, color: "#C5750A", letterSpacing: "2px", textTransform: "uppercase", marginBottom: 8 }}>
                          Matched to: Knee Replacement Recovery
                        </div>

                        {/* Support groups */}
                        {[
                          { emoji: "🦵", name: "Knee Warriors Community", members: "4,812 members", tag: "Peer Support", tagColor: "#C5750A" },
                          { emoji: "💰", name: "Hospital Bill Fighters", members: "12,340 members", tag: "Financial Help", tagColor: T.green },
                          { emoji: "🏥", name: "Uninsured Patient Network", members: "8,901 members", tag: "Patient Rights", tagColor: T.red },
                        ].map((g) => (
                          <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 8, background: isDark ? "#152240" : "white", border: `1px solid ${isDark ? "rgba(250,249,246,0.08)" : "#f0e8d8"}`, borderRadius: 8, padding: "7px 9px", marginBottom: 5, boxShadow: `0 1px 3px ${T.shadow}` }}>
                            <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{g.emoji}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: sans }}>{g.name}</div>
                              <div style={{ fontSize: 9, color: T.textMuted, fontFamily: sans, marginTop: 1 }}>{g.members}</div>
                            </div>
                            <div style={{ fontSize: 8, background: `${g.tagColor}15`, color: g.tagColor, padding: "2px 6px", borderRadius: 4, fontFamily: sans, fontWeight: 700, letterSpacing: "0.5px", flexShrink: 0 }}>{g.tag}</div>
                          </div>
                        ))}

                        {/* Programs banner */}
                        <div style={{ marginTop: 6, background: `linear-gradient(90deg, #C5750A, #D4883A)`, borderRadius: 8, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.8)", fontFamily: sans, letterSpacing: "1px", textTransform: "uppercase" }}>Programs Available to You</div>
                            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.65)", fontFamily: sans, marginTop: 1 }}>Medicaid expansion · Hill-Burton · HRSA · 3 more</div>
                          </div>
                          <div style={{ fontSize: 9, color: "white", fontWeight: 800, fontFamily: sans, flexShrink: 0, marginLeft: 10 }}>6 found →</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots + step labels */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 24, marginTop: 24 }}>
            {["Build Scenario", "Set up Family", "Find Route", "Plan with Dignity", "Get Connected"].map((label, i) => (
              <button key={i} onClick={() => setDemoSlide(i)} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: "pointer", padding: "4px 8px",
                opacity: demoSlide === i ? 1 : 0.4,
                transition: "opacity 0.3s",
              }}>
                <div style={{
                  width: demoSlide === i ? 28 : 8, height: 4, borderRadius: 2,
                  background: demoSlide === i ? T.red : T.textMuted,
                  transition: "all 0.4s ease",
                }} />
                <span style={{ fontFamily: sans, fontSize: 11, fontWeight: demoSlide === i ? 700 : 400, color: demoSlide === i ? T.text : T.textMuted, letterSpacing: "0.3px" }}>
                  {label}
                </span>
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* ─── DIGNITY — AI FINANCIAL ADVISOR ─── */}
      <>
        <style>{`
          @keyframes dignitySpinnerHP {
            to { transform: rotate(360deg); }
          }
          @keyframes dignityPulseHP {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          @keyframes dignityFloat {
            0%,100%{transform:translateY(0px) rotate(0deg)}
            35%{transform:translateY(-11px) rotate(-2.5deg)}
            68%{transform:translateY(-5px) rotate(1.8deg)}
          }
          @keyframes dignityBounce {
            0%,100%{transform:translateY(0) scaleY(1)}
            20%{transform:translateY(-18px) scaleY(1.04)}
            40%{transform:translateY(0) scaleY(0.97)}
            55%{transform:translateY(-8px) scaleY(1.02)}
            70%{transform:translateY(0) scaleY(1)}
          }
          @keyframes stampIn {
            0%{transform:scale(1.6) rotate(-12deg);opacity:0}
            55%{transform:scale(0.93) rotate(-8deg);opacity:1}
            100%{transform:scale(1) rotate(-8deg);opacity:1}
          }
          @keyframes scrollPulse {
            0%,100%{box-shadow:0 6px 20px rgba(178,34,52,0.28)}
            50%{box-shadow:0 8px 28px rgba(178,34,52,0.42)}
          }
          @keyframes starTwinkle {
            0%,100%{opacity:0.55;transform:scale(1)}
            50%{opacity:1;transform:scale(1.25)}
          }
          .dignity-wrap{animation:dignityFloat 4.2s ease-in-out infinite;display:inline-block;cursor:pointer}
          .dignity-wrap:hover{animation:dignityBounce 0.65s ease-in-out;filter:drop-shadow(0 16px 28px rgba(0,40,104,0.28))}
          .dignity-stamp{animation:stampIn 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.4s both}
          .dignity-scroll{animation:scrollPulse 3s ease-in-out infinite}
          .d-star-1{animation:starTwinkle 2.1s ease-in-out infinite}
          .d-star-2{animation:starTwinkle 2.1s ease-in-out 0.4s infinite}
          .d-star-3{animation:starTwinkle 2.1s ease-in-out 0.8s infinite}
          .d-star-4{animation:starTwinkle 2.1s ease-in-out 1.2s infinite}
          .d-star-5{animation:starTwinkle 2.1s ease-in-out 1.6s infinite}
          @keyframes dignityBlink{0%,100%{opacity:1}50%{opacity:0}}
          @keyframes dignityArrow{from{transform:translateY(0)}to{transform:translateY(5px)}}
        `}</style>

        <section
          style={{
            background: isDark
              ? `linear-gradient(170deg, #0d1e35 0%, #0a1628 55%, #0d1e35 100%)`
              : `linear-gradient(170deg, #F5F1E8 0%, #EDF2F9 55%, #F5F1E8 100%)`,
            borderTop: `3px solid ${T.blue}`,
            borderBottom: `3px solid ${T.blue}`,
            paddingTop: "clamp(48px, 6vw, 88px)",
            paddingBottom: "clamp(48px, 6vw, 88px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Star-field watermark */}
          <svg aria-hidden="true" style={{ position:"absolute",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none",opacity:0.04 }} preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="digStarPat" x="0" y="0" width="90" height="90" patternUnits="userSpaceOnUse">
                <polygon points="45,8 48,31 71,31 53,46 60,68 45,54 30,68 37,46 19,31 42,31" fill="#002868"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#digStarPat)"/>
          </svg>

          {/* Horizontal red/white/blue stripe accent at very top */}
          <div style={{ position:"absolute", top:0, left:0, right:0, height:6, display:"flex" }}>
            <div style={{ flex:1, background:"#B22234" }}/>
            <div style={{ flex:1, background:"white" }}/>
            <div style={{ flex:1, background:"#002868" }}/>
          </div>

          <div style={{ maxWidth:860, margin:"0 auto", padding:"0 clamp(24px, 4vw, 48px)", position:"relative" }}>

            {/* Bunting */}
            <div style={{ textAlign:"center", marginBottom:24, lineHeight:1 }}>
              <svg viewBox="0 0 800 60" width="100%" height="44" style={{ maxWidth:760, overflow:"visible" }}>
                <path d="M 10,14 Q 200,42 400,14 Q 600,42 790,14" fill="none" stroke="#3D2000" strokeWidth="1.5" opacity="0.4"/>
                {[40,100,160,220,280,340,400,460,520,580,640,700,760].map((x, i) => {
                  const c = ["#B22234","#FAF9F6","#002868"][i % 3];
                  const yTop = i % 2 === 0 ? 10 : 26;
                  return <polygon key={x} points={`${x-12},${yTop} ${x+12},${yTop} ${x},${yTop+22}`} fill={c} stroke="#3D2000" strokeWidth="1" opacity="0.85"/>;
                })}
                {[80,240,400,560,720].map((x, i) => (
                  <polygon key={`s${x}`} className={`d-star-${i+1}`}
                    points={`${x},2 ${x+3},9 ${x+10},9 ${x+4},14 ${x+6},21 ${x},17 ${x-6},21 ${x-4},14 ${x-10},9 ${x-3},9`}
                    fill="#C5A55A"/>
                ))}
              </svg>
            </div>

            {/* ── CENTERED DIGNITY CHARACTER ── */}
            <div style={{ textAlign:"center", marginBottom:0 }}>
              <div className="dignity-wrap" style={{ display:"inline-block", position:"relative" }}>
                {/* No WebGL → static PNG */}
                {!webglSupported && (
                  <img
                    src="/consumer/dignity-transparent.webp"
                    alt="Dignity — America's healthcare advisor"
                    style={{
                      width: "clamp(220px, 30vw, 360px)",
                      height: "auto",
                      display: "block",
                      filter: "drop-shadow(0 12px 32px rgba(0,40,104,0.25))",
                    }}
                  />
                )}
                {/* WebGL available but loading */}
                {webglSupported && !modelViewerReady && (
                  <div style={{
                    width: "clamp(300px, 38vw, 480px)",
                    height: "clamp(350px, 44vw, 560px)",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 14,
                  }}>
                    <div style={{
                      width: 64, height: 64,
                      border: `3px solid ${isDark ? "rgba(250,249,246,0.1)" : "#e5e2da"}`,
                      borderTopColor: T.gold,
                      borderRightColor: T.red,
                      borderBottomColor: T.blue,
                      borderRadius: "50%",
                      animation: "dignitySpinnerHP 1.2s linear infinite",
                    }} />
                    <div style={{
                      fontFamily: sans, fontSize: 11, fontWeight: 700,
                      color: T.gold, letterSpacing: "2.5px", textTransform: "uppercase",
                      animation: "dignityPulseHP 2s ease-in-out infinite",
                    }}>
                      Loading Dignity...
                    </div>
                  </div>
                )}
                {/* WebGL ready → 3D model */}
                {webglSupported && modelViewerReady && (
                  <model-viewer
                    ref={dignityViewerRef as any}
                    src="/consumer/dignity_bill.glb"
                    alt="Dignity — America's healthcare advisor"
                    camera-controls
                    shadow-intensity="1"
                    exposure="1.1"
                    interaction-prompt="none"
                    camera-orbit="0deg 75deg 105%"
                    style={{
                      width: "clamp(300px, 38vw, 480px)",
                      height: "clamp(350px, 44vw, 560px)",
                      background: "transparent",
                      display: "block",
                    } as React.CSSProperties}
                  />
                )}
              </div>
            </div>

            {/* ── VISUAL NOVEL — DignityNarrationPlayer ── */}
            <div style={{ marginBottom: 36 }}>
              <DignityNarrationPlayer pageKey="homepage" />
            </div>

            {/* ── HEADLINE + SUBHEAD ── */}
            <div style={{ textAlign:"center", marginBottom:28 }}>
              <h2 style={{
                fontFamily:serif,
                fontSize:"clamp(36px, 5vw, 58px)",
                fontWeight:400,
                color:T.text,
                letterSpacing:"-1.5px",
                lineHeight:1.05,
                marginBottom:6,
              }}>
                {t('homepage:dignity.title')}
              </h2>
              <p style={{
                fontFamily:serif,
                fontSize:"clamp(16px, 2vw, 21px)",
                color:T.blue,
                fontStyle:"italic",
                lineHeight:1.35,
              }}>
                {t('homepage:dignity.subtitle')}
              </p>
            </div>

            {/* ── BODY COPY ── */}
            <p style={{ fontSize:"clamp(14px, 1.6vw, 16px)", color:T.textMuted, lineHeight:1.8, maxWidth:560, margin:"0 auto 28px", textAlign:"center" }}>
              {t('homepage:dignity.body')}
            </p>

            {/* ── DIGNITY'S MISSION — Three pillars ── */}
            <div style={{ display:"flex", gap:14, marginBottom:28, flexWrap:"wrap", justifyContent:"center", alignItems:"stretch" }}>

              {/* Pillar 1 — Educational Guides — HIDDEN 2026-05-11.
                  Needs rework with the new Dignity-curated column info
                  (audit notes, canonical units, cornerstone reasoning)
                  surfaced from scenario_procedures.cornerstone_notes +
                  quantity_audit_notes. Re-enable once the new template
                  is wired. */}

              {/* Pillar 2 — Your Advisor */}
              <Link to="/scenarios?chat=dignity" style={{ textDecoration:"none", flex:"1 1 180px", maxWidth:220 }}>
                <div style={{
                  height:"100%",
                  border:`2.5px solid ${T.blue}`, borderRadius:10,
                  background: isDark ? T.cardBg : "white", padding:"18px 18px",
                  boxShadow:`0 4px 18px rgba(0,40,104,0.14)`,
                  textAlign:"center", transform:"rotate(-1.5deg)",
                  transition:"transform 0.2s, box-shadow 0.2s",
                  cursor:"pointer",
                }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>🩺</div>
                  <div style={{ fontFamily:sans, fontSize:10, fontWeight:800, color:T.blue, letterSpacing:"2px", textTransform:"uppercase", marginBottom:6 }}>{t('homepage:dignity.advisorTitle')}</div>
                  <div style={{ fontFamily:sans, fontSize:12, color:T.text, lineHeight:1.55 }}>
                    {t('homepage:dignity.advisorDesc')}
                  </div>
                </div>
              </Link>

              {/* Pillar 3 — The Movement */}
              <Link to="/about/dignity" style={{ textDecoration:"none", flex:"1 1 180px", maxWidth:220 }}>
                <div style={{
                  height:"100%",
                  border:`2.5px solid ${T.gold}`, borderRadius:10,
                  background: isDark ? T.cardBg : "white", padding:"18px 18px",
                  boxShadow:`0 4px 18px rgba(197,165,90,0.18)`,
                  textAlign:"center", transform:"rotate(1.5deg)",
                  transition:"transform 0.2s, box-shadow 0.2s",
                  cursor:"pointer",
                  position:"relative",
                }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>✊</div>
                  <div style={{ fontFamily:sans, fontSize:10, fontWeight:800, color:T.gold, letterSpacing:"2px", textTransform:"uppercase", marginBottom:6 }}>{t('homepage:dignity.movementTitle')}</div>
                  <div style={{ fontFamily:sans, fontSize:12, color:T.text, lineHeight:1.55 }}>
                    {t('homepage:dignity.movementDesc')}
                  </div>
                </div>
              </Link>
            </div>

            {/* ── PILLS ── */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:28, justifyContent:"center" }}>
              {[t('homepage:dignity.pill1'), t('homepage:dignity.pill2'), t('homepage:dignity.pill3')].map((p) => (
                <span key={p} style={{
                  padding:"5px 14px", borderRadius:20, border:`1.5px solid ${T.border}`,
                  fontSize:12, fontFamily:sans, color:T.text, background: isDark ? "rgba(13,30,53,0.7)" : "rgba(255,255,255,0.7)",
                }}>{p}</span>
              ))}
              <span style={{ padding:"5px 14px", borderRadius:20, border:`1.5px solid ${T.border}`, fontSize:12, fontFamily:sans, color:T.textMuted, fontStyle:"italic", background: isDark ? "rgba(13,30,53,0.7)" : "rgba(255,255,255,0.7)" }}>
                ⓘ {t('homepage:dignity.aiDisclaimer')}
              </span>
            </div>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <Link
                to="/scenarios"
                style={{
                  display:"inline-flex", alignItems:"center", gap:10,
                  padding:"14px 34px",
                  background:`linear-gradient(135deg, ${T.blue} 0%, #003A9E 100%)`,
                  color:"white", fontFamily:sans, fontSize:15, fontWeight:600,
                  letterSpacing:"0.5px", borderRadius:6, textDecoration:"none",
                  boxShadow:"0 6px 24px rgba(0,40,104,0.3)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 10px 32px rgba(0,40,104,0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 6px 24px rgba(0,40,104,0.3)"; }}
              >
                <span style={{ fontSize:18 }}>📄</span>
                Browse Scenarios
                <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link
                to="/donate"
                style={{
                  display:"inline-flex", alignItems:"center", gap:10,
                  padding:"14px 34px",
                  background:`linear-gradient(135deg, ${T.gold} 0%, #B8943A 100%)`,
                  color:"white", fontFamily:sans, fontSize:15, fontWeight:600,
                  letterSpacing:"0.5px", borderRadius:6, textDecoration:"none",
                  boxShadow:"0 6px 24px rgba(197,165,90,0.3)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 10px 32px rgba(197,165,90,0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 6px 24px rgba(197,165,90,0.3)"; }}
              >
                <span style={{ fontSize:18 }}>&#9733;</span>
                Support Our Mission
                <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          </div>

          {/* Bottom stripe mirror */}
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:6, display:"flex" }}>
            <div style={{ flex:1, background:"#002868" }}/>
            <div style={{ flex:1, background:"white" }}/>
            <div style={{ flex:1, background:"#B22234" }}/>
          </div>
        </section>
      </>

      {/* ─── COMMON MEDICAL SCENARIOS (Grid + Search + Pagination / Carousel) ─── */}
      <section
        ref={scenarioGridRef}
        className="relative flex flex-col"
        style={{ paddingTop: "clamp(40px, 5vw, 72px)", paddingBottom: "clamp(24px, 3vw, 40px)" }}
      >
        {/* Header — constrained width for all modes */}
        <div style={{ maxWidth: viewportTier === "ultrawide" ? "100%" : 1400, margin: "0 auto", width: "100%", padding: "0 48px" }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2
                style={{
                  fontFamily: serif,
                  fontSize: "clamp(28px, 4vw, 48px)",
                  fontWeight: 400,
                  letterSpacing: "-0.5px",
                }}
              >
                {t('homepage:scenarios.title')}
              </h2>
              <p className="mt-2" style={{ color: T.textMuted, fontSize: "clamp(14px, 1.8vw, 17px)" }}>
                {t('homepage:scenarios.subtitle').split('<mono>').map((part: string, i: number) => {
                  if (i === 0) return part;
                  const [inner, rest] = part.split('</mono>');
                  return <span key={i}><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>{inner}</span>{rest}</span>;
                })}
                {filteredScenarios.length > 0 && (
                  <span style={{ color: T.textMuted, marginLeft: 8, fontSize: 14 }}>
                    ({filteredScenarios.length} scenario{filteredScenarios.length !== 1 ? "s" : ""})
                  </span>
                )}
              </p>
            </div>

            {/* Search bar */}
            <div className="relative w-full md:w-80 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.textMuted }} />
              <input
                type="text"
                value={scenarioSearch}
                onChange={(e) => setScenarioSearch(e.target.value)}
                placeholder="Search scenarios..."
                className="w-full pl-10 pr-9 py-2.5 rounded-lg outline-none transition-colors"
                style={{
                  fontFamily: sans,
                  fontSize: 14,
                  color: T.text,
                  border: `1px solid ${T.border}`,
                  backgroundColor: T.searchBg,
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = T.red)}
                onBlur={(e) => (e.currentTarget.style.borderColor = T.border)}
              />
              {scenarioSearch && (
                <button
                  onClick={() => setScenarioSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70"
                >
                  <X className="w-4 h-4" style={{ color: T.textMuted }} />
                </button>
              )}
            </div>
          </div>

          {/* ─── Category filter pills with arrow navigation ─── */}
          <div className="relative" style={{ marginBottom: 8 }}>
            {/* Left fade + arrow */}
            {pillsCanScrollLeft && (
              <button
                onClick={() => scrollPills("left")}
                className="absolute left-0 top-0 bottom-4 z-10 flex items-center pl-0.5 pr-2 transition-opacity"
                style={{
                  background: `linear-gradient(to right, ${T.bg} 60%, transparent)`,
                }}
                aria-label="Scroll categories left"
              >
                <ChevronLeft className="w-5 h-5" style={{ color: T.text }} />
              </button>
            )}

            {/* Scrollable pills */}
            <div
              ref={pillsRef}
              className="flex gap-2 overflow-x-auto pb-4 scrollbar-none"
              style={{ scrollbarWidth: "none" }}
            >
              {SCENARIO_CATEGORIES.map((cat) => {
                const catCount = scenarios.filter((s) => scenarioMatchesCategory(s.tags, cat)).length;
                if (cat.key !== "all" && catCount === 0) return null;
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex-shrink-0"
                    style={{
                      backgroundColor: isActive ? T.red : T.cardBg,
                      color: isActive ? "#fff" : T.text,
                      border: `1px solid ${isActive ? T.red : T.border}`,
                      fontFamily: sans,
                    }}
                  >
                    {t(`scenarios:categories.${cat.i18nKey}`, cat.label)}
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: isActive ? "rgba(255,255,255,0.2)" : "rgba(27,27,27,0.06)",
                        fontSize: 11,
                        fontWeight: 600,
                        minWidth: 20,
                        textAlign: "center",
                      }}
                    >
                      {cat.key === "all" ? scenarios.length : catCount}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Right fade + arrow */}
            {pillsCanScrollRight && (
              <button
                onClick={() => scrollPills("right")}
                className="absolute right-0 top-0 bottom-4 z-10 flex items-center pr-0.5 pl-2 transition-opacity"
                style={{
                  background: `linear-gradient(to left, ${T.bg} 60%, transparent)`,
                }}
                aria-label="Scroll categories right"
              >
                <ChevronRight className="w-5 h-5" style={{ color: T.text }} />
              </button>
            )}
          </div>
        </div>

        {/* Scenario content area */}
        {scenariosLoading ? (
          <div className="flex-1 flex items-center justify-center py-16" style={{ color: T.textMuted }}>
            {t('homepage:scenarios.loading')}
          </div>
        ) : filteredScenarios.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
            <Search className="w-10 h-10 mb-4" style={{ color: T.border }} />
            <p style={{ fontFamily: serif, fontSize: 22, color: T.text, marginBottom: 4 }}>{t('homepage:scenarios.noResults')}</p>
            <p style={{ fontSize: 14, color: T.textMuted }}>
              {t('homepage:scenarios.noResultsHint')}{" "}
              <button onClick={() => { setScenarioSearch(""); setActiveCategory("all"); }} style={{ color: T.red, textDecoration: "underline" }}>
                {t('common:actions.clearFilter')}
              </button>
            </p>
          </div>
        ) : viewportTier === "ultrawide" && filteredScenarios.length >= 3 ? (
          /* ─── ULTRA-WIDE: Embla Carousel ─── */
          <div className="relative" style={{ width: "100vw", marginLeft: "calc(-50vw + 50%)" }}>
            {/* Carousel viewport */}
            <div
              className="overflow-hidden cursor-grab active:cursor-grabbing"
              ref={emblaRef}
              style={{ padding: "0 48px" }}
            >
              <div className="flex" style={{ gap: 20 }}>
                {carouselScenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    className="flex-shrink-0"
                    style={{ width: "calc((100vw - 96px - 40px) / 3)" }}
                  >
                    {renderScenarioCard(scenario)}
                  </div>
                ))}
              </div>
            </div>

            {/* Left arrow overlay */}
            <button
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canScrollPrev}
              className="absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center transition-all disabled:opacity-0"
              style={{
                left: 8,
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.95)",
                border: `1px solid ${T.border}`,
                boxShadow: `0 2px 12px ${T.shadow}`,
                color: T.text,
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Right arrow overlay */}
            <button
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canScrollNext}
              className="absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center transition-all disabled:opacity-0"
              style={{
                right: 8,
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.95)",
                border: `1px solid ${T.border}`,
                boxShadow: `0 2px 12px ${T.shadow}`,
                color: T.text,
              }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        ) : viewportTier === "ultrawide" && filteredScenarios.length < 3 ? (
          /* ─── ULTRA-WIDE with < 3 cards: centered flex ─── */
          <div className="flex justify-center gap-5 flex-1" style={{ padding: "0 48px" }}>
            {filteredScenarios.map((scenario) => (
              <div key={scenario.id} style={{ width: "calc((100vw - 96px - 40px) / 3)", maxWidth: 600 }}>
                {renderScenarioCard(scenario)}
              </div>
            ))}
          </div>
        ) : (
          /* ─── NORMAL / WIDE: Paginated grid ─── */
          <div style={{ maxWidth: viewportTier === "wide" ? 1800 : 1400, margin: "0 auto", width: "100%", padding: "0 48px" }}>
            <div className={`grid gap-5 flex-1 ${viewportTier === "wide" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
              {paginatedScenarios.map((scenario) => (
                <div key={scenario.id}>
                  {renderScenarioCard(scenario)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Pagination Bar (sticky bottom) — hidden in carousel mode ─── */}
        {!scenariosLoading && viewportTier !== "ultrawide" && filteredScenarios.length > scenariosPerPage && (
          <div
            className="sticky bottom-0 z-30 mt-6"
            style={{
              backgroundColor: isDark ? "rgba(10,22,40,0.92)" : "rgba(250,249,246,0.92)",
              backdropFilter: "blur(12px)",
              borderTop: `1px solid ${T.border}`,
              padding: "12px 0",
            }}
          >
            <div style={{ maxWidth: viewportTier === "wide" ? 1800 : 1400 }} className="mx-auto px-6 sm:px-12 flex items-center justify-between">
              {/* Left: page info */}
              <p style={{ fontSize: 13, color: T.textMuted, fontFamily: sans }}>
                Showing {scenarioPage * scenariosPerPage + 1}–{Math.min((scenarioPage + 1) * scenariosPerPage, filteredScenarios.length)} of {filteredScenarios.length}
              </p>

              {/* Center: page dots / numbers — smart-compact pagination.
                  Renders only first, last, current, and current±2 with
                  ellipses in between. Previous version blindly rendered
                  every page (~1,800 buttons at 36px each = ~66k px wide,
                  causing massive horizontal page overflow and a stray
                  bottom scrollbar). */}
              <div className="flex items-center gap-1 flex-wrap justify-center max-w-full">
                <button
                  onClick={() => {
                    setScenarioPage((p) => Math.max(0, p - 1));
                    scenarioGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  disabled={scenarioPage === 0}
                  className="p-2 rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: T.text }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {(() => {
                  // Compact page set: 0, around current ±2, totalScenarioPages-1.
                  // Dedup + sort, then walk inserting ellipses where there's a gap.
                  const pages = new Set<number>([
                    0,
                    totalScenarioPages - 1,
                    scenarioPage - 2, scenarioPage - 1, scenarioPage,
                    scenarioPage + 1, scenarioPage + 2,
                  ]);
                  const visible = [...pages]
                    .filter((n) => n >= 0 && n < totalScenarioPages)
                    .sort((a, b) => a - b);
                  const nodes: React.ReactNode[] = [];
                  let prev = -1;
                  for (const i of visible) {
                    if (prev >= 0 && i > prev + 1) {
                      nodes.push(
                        <span key={`ellipsis-${prev}`} className="px-1 text-sm" style={{ color: T.textMuted, fontFamily: sans }}>…</span>
                      );
                    }
                    nodes.push(
                      <button
                        key={i}
                        onClick={() => {
                          setScenarioPage(i);
                          scenarioGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        className="w-8 h-8 rounded-lg text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: scenarioPage === i ? T.red : "transparent",
                          color: scenarioPage === i ? "#fff" : T.text,
                          fontFamily: sans,
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                    prev = i;
                  }
                  return nodes;
                })()}
                <button
                  onClick={() => {
                    setScenarioPage((p) => Math.min(totalScenarioPages - 1, p + 1));
                    scenarioGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  disabled={scenarioPage >= totalScenarioPages - 1}
                  className="p-2 rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: T.text }}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Right: View All link */}
              <Link
                to="/scenarios"
                className="flex items-center gap-1.5 text-sm transition-colors"
                style={{ color: T.red, fontFamily: sans }}
              >
                {t('common:actions.viewAll')} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* ─── BROWSE BY CATEGORY (Netflix-style rows) ─── */}
      {!scenariosLoading && scenarios.length > 0 && (
        <section
          style={{
            paddingTop: "clamp(24px, 3vw, 40px)",
            paddingBottom: "clamp(24px, 3vw, 40px)",
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <div style={{ maxWidth: viewportTier === "ultrawide" ? "100%" : viewportTier === "wide" ? 1800 : 1400, margin: "0 auto", width: "100%" }}>
            <h2
              className="mb-8 px-12"
              style={{
                fontFamily: serif,
                fontSize: "clamp(24px, 3.5vw, 40px)",
                fontWeight: 400,
                letterSpacing: "-0.5px",
                color: T.text,
              }}
            >
              {t('homepage:scenarios.browseByCategory')}
            </h2>
            {SCENARIO_CATEGORIES.filter((cat) => cat.key !== "all").map((cat) => {
              const catScenarios = categoryScenarioMap[cat.key] || [];
              if (catScenarios.length < 1) return null;
              return (
                <LazyCategoryRow
                  key={cat.key}
                  label={cat.browseLabel}
                  color={cat.color}
                  count={catScenarios.length}
                  scenarios={catScenarios}
                  renderCard={(s: any) => renderScenarioCard(s, true)}
                  viewportTier={viewportTier}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Sentinel: marks end of scenarios zone for sticky search */}
      <div ref={scenarioEndRef} />


      {/* ─── STATS / TRUST BAR ─── */}
      <section
        style={{
          backgroundColor: T.bgDark,
          paddingTop: "clamp(48px, 6vw, 80px)",
          paddingBottom: "clamp(48px, 6vw, 80px)",
        }}
      >
        <div className="max-w-[1260px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { value: "546M+", label: t('homepage:stats.pricesAnalyzed') },
              { value: (stats?.hospitals ?? 9582).toLocaleString(), label: t('homepage:stats.hospitalsTracked') },
              { value: "50", label: t('homepage:stats.statesCovered') },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  style={{
                    fontFamily: serif,
                    fontSize: "clamp(36px, 5vw, 64px)",
                    color: "#fff",
                    fontWeight: 400,
                    letterSpacing: "-1px",
                  }}
                >
                  {stat.value}
                </div>
                <div style={{ fontSize: 16, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMMUNITY STORIES CTA ─── */}
      <section style={{ background: T.bgDark, paddingTop: "clamp(48px, 6vw, 80px)", paddingBottom: "clamp(48px, 6vw, 80px)" }}>
        <div className="max-w-[800px] mx-auto px-6 sm:px-12 text-center">
          <h2
            style={{
              fontFamily: serif,
              fontSize: "clamp(28px, 4vw, 48px)",
              fontWeight: 400,
              letterSpacing: "-0.5px",
              lineHeight: 1.15,
              color: "#FFFFFF",
              marginBottom: 16,
            }}
          >
            {t('homepage:community.title')}
          </h2>
          <p style={{ fontSize: "clamp(15px, 1.8vw, 18px)", color: "rgba(255,255,255,0.70)", lineHeight: 1.7, marginBottom: 32 }}>
            {t('homepage:community.subtitle').split('<gold>').map((part: string, i: number) => {
              if (i === 0) return part;
              const [inner, rest] = part.split('</gold>');
              return <span key={i}><span style={{ color: T.gold, fontWeight: 600 }}>{inner}</span>{rest}</span>;
            })}
          </p>
          <Link
            to="/stories"
            className="inline-flex items-center gap-2 px-8 py-4 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: T.red,
              color: "#fff",
              borderRadius: 8,
              fontFamily: sans,
              fontSize: 15,
              fontWeight: 500,
              letterSpacing: "0.5px",
            }}
          >
            {t('homepage:community.viewStories')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ─── BOTTOM CTA ─── */}
      <section style={{ paddingTop: "clamp(64px, 8vw, 120px)", paddingBottom: "clamp(64px, 8vw, 120px)" }}>
        <div className="max-w-[800px] mx-auto px-6 sm:px-12 text-center">
          <h2
            style={{
              fontFamily: serif,
              fontSize: "clamp(32px, 5vw, 64px)",
              fontWeight: 400,
              letterSpacing: "-1px",
              lineHeight: 1.1,
              marginBottom: 24,
            }}
          >
            {t('homepage:bottomCta.title1')}<br />{t('homepage:bottomCta.title2')}
          </h2>
          <p style={{ fontSize: "clamp(16px, 2vw, 20px)", color: T.textMuted, lineHeight: 1.7, marginBottom: 40 }}>
            {t('homepage:bottomCta.subtitle')}
          </p>
          <div ref={bottomAcRef} style={{ position: "relative" }} className="max-w-3xl mx-auto">
            <form onSubmit={(e) => { handleSearch(e); setBottomAcOpen(false); }}>
              <div
                className="flex flex-col items-stretch"
                style={{
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  backgroundColor: T.searchBg,
                  boxShadow: `0 4px 24px ${T.shadow}`,
                  overflow: "visible",
                  position: "relative",
                }}
              >
                {/* Top row: category dropdown + search input */}
                <div className="flex items-stretch flex-1">
                {/* Category dropdown */}
                <div ref={bottomDropdownRef} className="relative flex-shrink-0" style={{ zIndex: 20 }}>
                  <button
                    type="button"
                    onClick={() => setBottomDropdownOpen((v) => !v)}
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-4 h-full transition-colors"
                    style={{
                      fontFamily: sans,
                      fontSize: 14,
                      fontWeight: 500,
                      color: T.blue,
                      backgroundColor: bottomDropdownOpen ? "rgba(0,40,104,0.04)" : "transparent",
                      borderRight: `1px solid ${T.border}`,
                      borderRadius: "12px 0 0 0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{catLabel(selectedCategory.labelKey)}</span>
                    <ChevronDown
                      className="w-4 h-4 transition-transform"
                      style={{
                        color: T.textMuted,
                        transform: bottomDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>
                  {bottomDropdownOpen && (
                    <div
                      className="absolute bottom-full left-0 mb-1 py-1"
                      style={{
                        backgroundColor: T.cardBg,
                        border: `1px solid ${T.border}`,
                        borderRadius: 10,
                        boxShadow: `0 -8px 32px ${T.shadow}`,
                        minWidth: 180,
                        zIndex: 50,
                      }}
                    >
                      {SEARCH_CATEGORIES.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => {
                            setSearchCategory(cat.value);
                            setBottomDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 transition-colors flex items-center gap-2"
                          style={{
                            fontFamily: sans,
                            fontSize: 14,
                            color: searchCategory === cat.value ? T.red : T.text,
                            backgroundColor: searchCategory === cat.value ? "rgba(178,34,52,0.04)" : "transparent",
                            fontWeight: searchCategory === cat.value ? 600 : 400,
                          }}
                          onMouseEnter={(e) => {
                            if (searchCategory !== cat.value) e.currentTarget.style.backgroundColor = T.hoverBg;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = searchCategory === cat.value ? "rgba(178,34,52,0.04)" : "transparent";
                          }}
                        >
                          {searchCategory === cat.value && (
                            <span style={{ color: T.red, fontSize: 10 }}>&#9679;</span>
                          )}
                          {catLabel(cat.labelKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Search input */}
                <div className="flex items-center gap-3 flex-1 px-4 sm:px-5 min-w-0">
                  <Search className="w-5 h-5 flex-shrink-0" style={{ color: T.textMuted }} />
                  <input
                    type="text"
                    value={procedure}
                    onChange={(e) => setProcedure(e.target.value)}
                    onKeyDown={handleBottomAcKeyDown}
                    onFocus={() => { if (acResults.length > 0) setBottomAcOpen(true); }}
                    placeholder={catPlaceholder(selectedCategory.placeholderKey)}
                    className="w-full py-4 outline-none bg-transparent"
                    style={{ fontFamily: sans, fontSize: 16, color: T.text }}
                    autoComplete="off"
                  />
                </div>
                </div>

                {/* Submit (full width bottom row) */}
                <button
                  type="submit"
                  className="px-8 py-3 sm:py-4 font-medium transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: T.red,
                    color: "#fff",
                    fontFamily: sans,
                    fontSize: 15,
                    letterSpacing: "0.5px",
                    borderRadius: "0 0 12px 12px",
                    borderTop: `1px solid rgba(178,34,52,0.3)`,
                  }}
                >
                  {t('common:actions.search')}
                </button>
              </div>
            </form>

            {/* ─── BOTTOM CTA AUTOCOMPLETE DROPDOWN ─── */}
            {bottomAcOpen && acResults.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  right: 0,
                  marginBottom: 6,
                  backgroundColor: T.cardBg,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  boxShadow: `0 -12px 48px rgba(0,0,0,0.15), 0 -2px 8px rgba(0,0,0,0.08)`,
                  zIndex: 100,
                  overflow: "hidden",
                  maxHeight: 420,
                  overflowY: "auto",
                }}
              >
                {/* Category indicator */}
                <div
                  style={{
                    padding: "10px 16px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: sans,
                    color: T.blue,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    borderBottom: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: T.red, display: "inline-block" }} />
                  {catLabel(selectedCategory.labelKey)} {t('homepage:hero.results')}
                  {acLoading && (
                    <span style={{ marginLeft: "auto", fontSize: 10, color: T.textMuted, fontWeight: 400 }}>{t('common:searching')}</span>
                  )}
                </div>

                {/* Results */}
                {acResults.map((item, idx) => (
                  <button
                    key={item.id + idx}
                    type="button"
                    onClick={() => { handleAcSelect(item); setBottomAcOpen(false); }}
                    onMouseEnter={() => setBottomAcSelected(idx)}
                    className="w-full text-left transition-colors"
                    style={{
                      padding: "12px 16px",
                      fontFamily: sans,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      backgroundColor: bottomAcSelected === idx ? "rgba(0,40,104,0.04)" : "transparent",
                      borderBottom: idx < acResults.length - 1 ? `1px solid rgba(0,0,0,0.04)` : "none",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        marginTop: 2,
                        minWidth: 24,
                        textAlign: "center",
                        filter: bottomAcSelected === idx ? "none" : "grayscale(0.3)",
                      }}
                    >
                      {item.icon || "\u2022"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: T.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.label}
                        </span>
                        {item.price && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: T.green,
                              fontFamily: "'JetBrains Mono', monospace",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            {item.price}
                          </span>
                        )}
                      </div>
                      {item.sublabel && (
                        <div
                          style={{
                            fontSize: 13,
                            color: T.textMuted,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.sublabel}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: T.blue, marginTop: 3, fontWeight: 500, opacity: 0.6 }}>
                        {item.category}
                      </div>
                    </div>
                    <ArrowRight
                      className="flex-shrink-0"
                      style={{
                        width: 14,
                        height: 14,
                        color: bottomAcSelected === idx ? T.red : T.textMuted,
                        marginTop: 4,
                        transition: "color 0.15s, transform 0.15s",
                        transform: bottomAcSelected === idx ? "translateX(2px)" : "none",
                      }}
                    />
                  </button>
                ))}

                {/* Keyboard hint */}
                <div
                  style={{
                    padding: "8px 16px",
                    fontSize: 11,
                    color: T.textMuted,
                    borderTop: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontFamily: sans,
                  }}
                >
                  <span>
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>&uarr;</kbd>
                    {" "}
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>&darr;</kbd>
                    {" "}to navigate
                  </span>
                  <span>
                    <kbd style={{ padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>Enter</kbd>
                    {" "}to select
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="text-center pb-24 md:pb-8" style={{ color: T.textMuted, fontSize: 14 }}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <span style={{ color: T.red, fontSize: 18 }}>&#9733;</span>
          <span style={{ color: T.blue, fontSize: 18 }}>&#9733;</span>
          <span style={{ color: T.red, fontSize: 18 }}>&#9733;</span>
        </div>
        <p style={{ fontFamily: serif, fontStyle: "italic", fontSize: 16, color: T.blue }}>
          American Hospital Prices
        </p>
        <p className="mt-2" style={{ fontSize: 13, color: T.textMuted }}>
          {t('homepage:footer.tagline')}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-4 px-6" style={{ fontSize: 12 }}>
          <a href="/terms" style={{ color: T.textMuted, textDecoration: 'none' }} className="hover:underline">{t('common:nav.terms')}</a>
          <span style={{ color: T.textMuted }}>|</span>
          <a href="/privacy" style={{ color: T.textMuted, textDecoration: 'none' }} className="hover:underline">{t('common:nav.privacy')}</a>
          <span style={{ color: T.textMuted }}>|</span>
          <a href="/data-sources" style={{ color: T.textMuted, textDecoration: 'none' }} className="hover:underline">{t('common:nav.dataSources')}</a>
          <span style={{ color: T.textMuted }}>|</span>
          <a href="/data-methodology" style={{ color: T.textMuted, textDecoration: 'none' }} className="hover:underline">{t('common:nav.methodology')}</a>
        </div>
        <p className="mt-3" style={{ fontSize: 11, color: T.textMuted }}>
          &copy; {new Date().getFullYear()} {brand.name}. {t('common:common.allRightsReserved')}
        </p>
      </footer>

      {/* ─── DEBUG PANEL (Ctrl+D) ─── */}
      {showDebug && (
        <div
          className="fixed bottom-24 right-4 z-[99999] rounded-xl shadow-2xl"
          style={{
            backgroundColor: "rgba(0,0,0,0.92)",
            color: "#fff",
            fontFamily: sans,
            fontSize: 13,
            width: 320,
            backdropFilter: "blur(12px)",
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
          >
            <span className="font-semibold text-sm">Hero Video Debug</span>
            <button
              onClick={() => setShowDebug(false)}
              className="text-white/50 hover:text-white text-lg leading-none"
            >
              &times;
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Video toggle */}
            <div className="flex items-center justify-between">
              <span className="text-white/70">Video enabled</span>
              <button
                onClick={() => setVideoEnabled((v) => !v)}
                className="px-3 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: videoEnabled ? "#2b5e49" : "rgba(255,255,255,0.15)",
                  color: "#fff",
                }}
              >
                {videoEnabled ? "ON" : "OFF"}
              </button>
            </div>

            {/* Video selector */}
            <div>
              <span className="text-white/70 block mb-2">Video source</span>
              <div className="flex gap-2">
                {HERO_VIDEOS.map((v, i) => (
                  <button
                    key={v.id}
                    onClick={() => setVideoIdx(i)}
                    className="flex-1 px-3 py-2 rounded text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: videoIdx === i ? T.red : "rgba(255,255,255,0.1)",
                      color: "#fff",
                    }}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Opacity slider */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/70">Opacity</span>
                <span className="text-white/50 font-mono">{(videoOpacity * 100).toFixed(0)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={videoOpacity * 100}
                onChange={(e) => setVideoOpacity(Number(e.target.value) / 100)}
                className="w-full accent-red-500"
                style={{ accentColor: T.red }}
              />
            </div>

            {/* Playback speed */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/70">Speed</span>
                <span className="text-white/50 font-mono">{videoPlaybackRate.toFixed(1)}x</span>
              </div>
              <input
                type="range"
                min="10"
                max="200"
                value={videoPlaybackRate * 100}
                onChange={(e) => setVideoPlaybackRate(Number(e.target.value) / 100)}
                className="w-full"
                style={{ accentColor: T.blue }}
              />
            </div>

            {/* Blend mode */}
            <div>
              <span className="text-white/70 block mb-2">Blend mode</span>
              <div className="flex flex-wrap gap-1">
                {["normal", "multiply", "screen", "overlay", "soft-light", "luminosity"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setBgBlend(mode)}
                    className="px-2 py-1 rounded text-[11px] transition-colors"
                    style={{
                      backgroundColor: bgBlend === mode ? T.gold : "rgba(255,255,255,0.08)",
                      color: bgBlend === mode ? "#000" : "rgba(255,255,255,0.6)",
                    }}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Current values summary */}
            <div
              className="text-[11px] text-white/30 pt-2 font-mono"
              style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
            >
              {HERO_VIDEOS[videoIdx].id} · {(videoOpacity * 100).toFixed(0)}% · {videoPlaybackRate.toFixed(1)}x · {bgBlend}
            </div>
          </div>
        </div>
      )}

      {/* ─── CREATE SCENARIO DIALOG (for "Use as Template") ─── */}
      <CreateScenarioDialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) setTemplateScenario(null);
        }}
        templateData={templateScenario}
      />

      {/* ─── FLOATING AUDIO BUTTON (video-game BGM toggle style) ─── */}
      <>
        <style>{`
          @keyframes audioPopupIn {
            from { opacity:0; transform:translateY(8px) scale(0.93); }
            to   { opacity:1; transform:translateY(0)  scale(1);    }
          }
          @keyframes audioNoteFloat {
            0%,100% { transform:translateY(0)   rotate(0deg)  }
            40%      { transform:translateY(-4px) rotate(-8deg) }
            70%      { transform:translateY(-2px) rotate(4deg)  }
          }
          .audio-float-btn {
            transition: transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s;
          }
          .audio-float-btn:hover { transform: scale(1.12) !important; }
          .audio-float-popup { animation: audioPopupIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both; }
          .audio-note-icon { animation: audioNoteFloat 2.2s ease-in-out infinite; display:block; }
          .audio-volume-range {
            -webkit-appearance: none;
            appearance: none;
            height: 4px;
            border-radius: 2px;
            outline: none;
            cursor: pointer;
            flex: 1;
          }
          .audio-volume-range::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 14px; height: 14px;
            border-radius: 50%;
            background: #C5A55A;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
            cursor: pointer;
          }
          .audio-volume-range::-moz-range-thumb {
            width: 14px; height: 14px;
            border-radius: 50%;
            background: #C5A55A;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
            cursor: pointer;
          }
        `}</style>
        <div style={{
          position: "fixed",
          bottom: 80,
          right: 37,
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 10,
          pointerEvents: "none",
        }}>
          {/* Volume popup panel */}
          {audioPopupOpen && (
            <div
              className="audio-float-popup"
              style={{
                background: "rgba(0,16,52,0.97)",
                border: `1.5px solid ${T.gold}`,
                borderRadius: 14,
                padding: "14px 16px 12px",
                boxShadow: "0 8px 40px rgba(0,16,52,0.6), inset 0 1px 0 rgba(197,165,90,0.2)",
                backdropFilter: "blur(14px)",
                minWidth: 168,
                pointerEvents: "auto",
              }}
            >
              {/* Panel header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}>
                <span style={{
                  fontFamily: sans,
                  fontSize: 9,
                  fontWeight: 800,
                  color: T.gold,
                  letterSpacing: "2.5px",
                  textTransform: "uppercase",
                }}>
                  ♪ Voice Volume
                </span>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  color: "rgba(197,165,90,0.7)",
                }}>
                  {Math.round(dignityVolume * 100)}%
                </span>
              </div>

              {/* Speaker + slider row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontSize: 16,
                  lineHeight: 1,
                  minWidth: 20,
                  textAlign: "center",
                  filter: dignityVolume === 0 ? "grayscale(1) opacity(0.4)" : "none",
                  transition: "filter 0.2s",
                }}>
                  {dignityVolume === 0 ? "🔇" : dignityVolume < 0.4 ? "🔈" : dignityVolume < 0.75 ? "🔉" : "🔊"}
                </span>
                <input
                  className="audio-volume-range"
                  type="range"
                  min={0} max={1} step={0.01}
                  value={dignityVolume}
                  onChange={e => {
                    setDignityVolume(parseFloat(e.target.value));
                  }}
                  style={{
                    background: `linear-gradient(to right, ${T.gold} ${dignityVolume * 100}%, rgba(197,165,90,0.18) ${dignityVolume * 100}%)`,
                  } as React.CSSProperties}
                />
              </div>

              {/* Dignity label */}
              <div style={{
                fontFamily: sans,
                fontSize: 10,
                color: "rgba(255,255,255,0.28)",
                marginTop: 9,
                textAlign: "center",
                letterSpacing: "0.5px",
              }}>
                Dignity's voice · Web Audio
              </div>
            </div>
          )}

          {/* Circular music toggle button */}
          <button
            className="audio-float-btn"
            onClick={() => setAudioPopupOpen(v => !v)}
            title={audioPopupOpen ? "Close volume" : "Voice volume"}
            aria-label="Voice volume control"
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: audioPopupOpen
                ? `linear-gradient(135deg, ${T.gold} 0%, #D4B86A 100%)`
                : `linear-gradient(135deg, ${T.blue} 0%, #0038B8 100%)`,
              border: `2px solid ${audioPopupOpen ? T.blue : T.gold}`,
              color: audioPopupOpen ? T.blue : T.gold,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: audioPopupOpen
                ? `0 4px 20px rgba(197,165,90,0.5), 0 0 0 3px rgba(197,165,90,0.15)`
                : `0 4px 20px rgba(0,40,104,0.45), 0 0 0 3px rgba(0,40,104,0.12)`,
              pointerEvents: "auto",
              padding: 0,
            }}
          >
            <span
              className="audio-note-icon"
              style={{
                fontSize: dignityVolume === 0 ? 20 : 22,
                lineHeight: 1,
              }}
            >
              {dignityVolume === 0 ? "🔇" : "♪"}
            </span>
          </button>
        </div>
      </>
    </div>
  );
};
